import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSaaSContext } from '../../store/SaaSContext';
import { useThreeDContext } from '../../store/ThreeDContext';
import { useToast } from '../../store/ToastContext';
import { ElementType, VdpElement, VdpTemplate, VdpLayout, Unit, Point } from '../../types';
import { Plus, Type, QrCode, Trash2, Settings2, Barcode as BarcodeIcon, Image as ImageIcon, Square, Circle, Minus, Star, Hexagon, Triangle, Diamond, Heart, ArrowRight, Scissors, Split, Flame, Hash, Calendar, DollarSign, ALargeSmall, ZoomIn, ZoomOut, Maximize2, Box, Eye, EyeOff, Layers, Copy, Lock, Unlock, ArrowUp, ArrowDown, Grid3X3, X, FolderOpen, Save, PenTool, Pencil, Check, ChevronDown, ChevronRight, FolderPlus, FolderMinus, PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen, Palette, MousePointer, Combine } from 'lucide-react';
import { formatUnit, parseUnit } from '../../utils/units';
import { Undo, Redo } from 'lucide-react';
import { PropertiesPanel } from './PropertiesPanel';
import { PriceTagSidebarManager } from './PriceTagSidebarManager';
import { TemplateManagerModal } from './TemplateManagerModal';
import { autoSyncCsvForTemplate } from '../../utils/templateCsvMapper';
import { getElementsBoundingBox } from '../../utils/quickExportUtils';
import { getLocalIsoDateString } from '../../utils/shapeAndFormatUtils';
import { BrandPaletteSection } from './BrandPaletteSection';
import { DEFAULT_BRAND_PALETTE } from '../../utils/brandPaletteUtils';


import { PathfinderPanel } from './PathfinderPanel';
import { applyPathfinderOperation } from '../../utils/pathfinderUtils';
import { PathfinderUnionIcon, PathfinderSubtractIcon, PathfinderIntersectIcon, PathfinderExcludeIcon } from './PathfinderIcons';
import { SimplifyPathModal } from './SimplifyPathModal';
import { SimplifyPathIcon } from './SimplifyPathIcon';
import { playfulAudio } from '../../utils/playfulAudio';
import { RulerAndGuides, GuideLine } from './RulerAndGuides';
import { TopAlignmentToolbar } from './CanvasAlignmentToolbar';
import { UnifiedContextualToolbar } from './UnifiedContextualToolbar';
import { DrawingToolbar } from './DrawingToolbar';
import { pointsToSvgPath, bezierPointsToSvgPath, getAnchorTangent } from '../../utils/vectorPathUtils';
import { CanvasElementItem } from './CanvasElementItem';
import { DatePickerModal } from './DatePickerModal';
import { LivePreviewModal } from './LivePreviewModal';
import { ProofViewer3DModal } from './ProofViewer3DModal';
import { Studio3DSplitViewer } from './Studio3DSplitViewer';
import { MacWindow } from './MacWindow';
import { motion, AnimatePresence } from 'motion/react';
import { Database } from 'lucide-react';
import { RecordSearchJumper } from './RecordSearchJumper';
import { macSpring, macPanelVariants, macPopUpVariants } from '../../utils/macAnimations';

// Grouping Helper Functions
const expandSelectionGroups = (ids: string[], elements: VdpElement[]): string[] => {
  const expanded = new Set(ids);
  elements.forEach(el => {
    if (el.groupId && expanded.has(el.id)) {
      elements.forEach(sub => {
        if (sub.groupId === el.groupId) expanded.add(sub.id);
      });
    }
  });
  return Array.from(expanded);
};

const getGroupElementIds = (elementId: string, elements: VdpElement[]): string[] => {
  const target = elements.find(e => e.id === elementId);
  if (target && target.groupId) {
    return elements.filter(e => e.groupId === target.groupId).map(e => e.id);
  }
  return [elementId];
};





type ToolItem = {
  type: string;
  label: string;
  icon: React.FC<any>;
  textClass?: string;
};

const ADD_ELEMENT_TOOLS: ToolItem[] = [

  { type: 'text', label: 'Text', icon: Type },
  { type: 'qr', label: 'QR', icon: QrCode },
  { type: 'barcode', label: 'Barcode', icon: BarcodeIcon },
  { type: 'image', label: 'Image', icon: ImageIcon },
  { type: 'rectangle', label: 'Rect', icon: Square },
  { type: 'circle', label: 'Circle', icon: Circle },
  { type: 'star', label: 'Star', icon: Star },
  { type: 'polygon', label: 'Poly', icon: Hexagon },
  { type: 'line', label: 'Line', icon: Minus },
  { type: 'datamatrix', label: 'DMatrix', icon: QrCode, textClass: 'text-[10px]' },
  { type: 'aztec', label: 'Aztec', icon: QrCode, textClass: 'text-[10px]' },
  { type: 'pdf417', label: 'PDF417', icon: BarcodeIcon, textClass: 'text-[10px]' },
  { type: 'ean13', label: 'EAN13', icon: BarcodeIcon },
  { type: 'upca', label: 'UPCA', icon: BarcodeIcon },
  { type: 'currency', label: 'Money', icon: DollarSign, textClass: 'text-[10px]' },
  { type: 'date', label: 'Date', icon: Calendar },
  { type: 'serial', label: 'Serial', icon: Hash }
];

const normalizeUpdates = (updates: Partial<VdpElement>): Partial<VdpElement> => {
  const normalized = { ...updates };
  if (updates.color !== undefined) normalized.fillColor = updates.color;
  if (updates.fillColor !== undefined) normalized.color = updates.fillColor;

  if (updates.borderColor !== undefined) normalized.strokeColor = updates.borderColor;
  if (updates.strokeColor !== undefined) normalized.borderColor = updates.strokeColor;

  if (updates.borderWidth !== undefined) normalized.strokeWidth = updates.borderWidth;
  if (updates.strokeWidth !== undefined) normalized.borderWidth = updates.strokeWidth;

  if (updates.borderRadius !== undefined) normalized.cornerRadius = updates.borderRadius;
  if (updates.cornerRadius !== undefined) normalized.borderRadius = updates.cornerRadius;

  return normalized;
};

export const DesignStep = () => {
  const { 
    template, 
    setTemplate, 
    csvData, 
    setCsvData, 
    appUnit, 
    setAppUnit, 
    layout, 
    setLayout,
    zoomLevel,
    setZoomLevel,
    panOffset,
    setPanOffset,
    hasAutoFitted,
    setHasAutoFitted,
    isFitToPage,
    setIsFitToPage
  } = useSaaSContext();
  const threeD = useThreeDContext();
  const { toast } = useToast();
  
  const templateRef = useRef(template);
  const selectedElementIdsRef = useRef<string[]>([]);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [resizingId, setResizingId] = useState<string | null>(null);
  const [resizeStart, setResizeStart] = useState<any>(null);
  const [rotatingId, setRotatingId] = useState<string | null>(null);

  useEffect(() => {
    templateRef.current = template;
  }, [template]);

  const [showTemplateManager, setShowTemplateManager] = useState<boolean>(false);
  const [showSaveModeInManager, setShowSaveModeInManager] = useState<boolean>(false);
  const [showLivePreviewModal, setShowLivePreviewModal] = useState<boolean>(false);
  const [showPathfinderPanel, setShowPathfinderPanel] = useState<boolean>(false);
  const [showSimplifyPathModal, setShowSimplifyPathModal] = useState<boolean>(false);
  const [simplifyPreviewElements, setSimplifyPreviewElements] = useState<VdpElement[] | null>(null);
  const [toastNotification, setToastNotification] = useState<string | null>(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  
  // Collapse States
  const [isLeftSidebarCollapsed, setIsLeftSidebarCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem('saas_design_left_collapsed');
    return saved === 'true';
  });
  
  const [isRightSidebarCollapsed, setIsRightSidebarCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem('saas_design_right_collapsed');
    return saved === 'true';
  });

  const [isSplit3DViewOpen, setIsSplit3DViewOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem('vdp_split_3d_open') === 'true';
    } catch {
      return false;
    }
  });

  const [gridDotSpacing, setGridDotSpacing] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('vdp_grid_dot_spacing');
      return saved ? Math.min(48, Math.max(8, Number(saved))) : 16;
    } catch {
      return 16;
    }
  });

  const handleUpdateGridDotSpacing = (val: number) => {
    setGridDotSpacing(val);
    try {
      localStorage.setItem('vdp_grid_dot_spacing', String(val));
    } catch {}
  };

  const [activeLeftFlyout, setActiveLeftFlyout] = useState<'settings' | 'palette' | 'elements' | 'variables' | null>(null);
  const [activeRightFlyout, setActiveRightFlyout] = useState<'properties' | 'layers' | 'prices' | null>(null);

  const toggleLeftSidebar = () => {
    const newVal = !isLeftSidebarCollapsed;
    setIsLeftSidebarCollapsed(newVal);
    localStorage.setItem('saas_design_left_collapsed', newVal.toString());
  };

  const toggleRightSidebar = () => {
    const newVal = !isRightSidebarCollapsed;
    setIsRightSidebarCollapsed(newVal);
    localStorage.setItem('saas_design_right_collapsed', newVal.toString());
  };

  const triggerToast = useCallback((msg: string) => {
    setToastNotification(msg);
    setTimeout(() => setToastNotification(null), 3000);
  }, []);

  const [selectedElementIds, setSelectedElementIds] = useState<string[]>([]);

  const selectedElementId = selectedElementIds.length === 1 ? selectedElementIds[0] : null;
  const selectedElements = useMemo(() => template.elements.filter(e => selectedElementIds.includes(e.id)), [template.elements, selectedElementIds]);
  useEffect(() => { selectedElementIdsRef.current = selectedElementIds; }, [selectedElementIds]);

  const frozenTemplate = useRef(template);
  const panelTemplate = useMemo(() => {
    if (draggingId || resizingId || rotatingId) {
      return frozenTemplate.current;
    }
    frozenTemplate.current = template;
    return template;
  }, [template, draggingId, resizingId, rotatingId]);

  const frozenSelectedElements = useRef(selectedElements);
  const panelSelectedElements = useMemo(() => {
    if (draggingId || resizingId || rotatingId) {
      return frozenSelectedElements.current;
    }
    frozenSelectedElements.current = selectedElements;
    return selectedElements;
  }, [selectedElements, draggingId, resizingId, rotatingId]);
  const [rotateStart, setRotateStart] = useState<any>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showLivePreview, setShowLivePreview] = useState(true);
  const [isPreviewCanvasActive, setIsPreviewCanvasActive] = useState(false);
  const [isHandToolActive, setIsHandToolActive] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [activeCreationTool, setActiveCreationTool] = useState<'textbox' | 'text' | 'rectangle' | 'circle' | 'star' | 'polygon' | 'triangle' | 'diamond' | 'heart' | 'arrow' | 'line' | null>(null);
  const [creationPreviewBox, setCreationPreviewBox] = useState<{ startX: number; startY: number; currentX: number; currentY: number } | null>(null);
  const stageContainerRef = useRef<HTMLDivElement>(null);
  const panStartPos = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const panStartScroll = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const handleResetView = () => {
    setPanOffset({ x: 0, y: 0 });
    setZoomLevel(1);
    setIsFitToPage(false);
  };

  const handleFitToPage = useCallback(() => {
    if (!stageContainerRef.current) return;
    const container = stageContainerRef.current;
    
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    if (containerWidth <= 0 || containerHeight <= 0) return;
    
    const tw = Number(template.width) || 300;
    const th = Number(template.height) || 200;
    
    // Add 80px total padding (40px per side)
    const padding = 80;
    const scaleX = (containerWidth - padding) / tw;
    const scaleY = (containerHeight - padding) / th;
    
    let newScale = Math.min(scaleX, scaleY);
    newScale = Math.max(0.1, Math.min(newScale, 5));
    
    setZoomLevel(Math.round(newScale * 100) / 100);
    setPanOffset({ x: 0, y: 0 });
    setIsFitToPage(true);
    setHasAutoFitted(true);
  }, [template.width, template.height, setZoomLevel, setPanOffset, setIsFitToPage, setHasAutoFitted]);

  // Persistent zoom & transformation restoration
  useEffect(() => {
    if (!hasAutoFitted) {
      const timer = setTimeout(() => {
        handleFitToPage();
      }, 80);
      return () => clearTimeout(timer);
    }
  }, [hasAutoFitted, handleFitToPage]);

  // Window resize handler: re-fit if in fit-to-page mode
  useEffect(() => {
    const handleWindowResize = () => {
      if (isFitToPage) {
        handleFitToPage();
      }
    };

    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [isFitToPage, handleFitToPage]);

  const handleStagePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const isMiddleClick = e.button === 1 || e.buttons === 4;
    if (isHandToolActive || isMiddleClick) {
      if (isMiddleClick) {
        e.preventDefault();
      }
      setIsPanning(true);
      panStartPos.current = { x: e.clientX, y: e.clientY };
      panStartScroll.current = { x: panOffset.x, y: panOffset.y };
      try {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      } catch (_) {}
    }
  };

  useEffect(() => {
    if (!isPanning) return;

    let animFrame: number | null = null;
    const handleGlobalPointerMove = (e: PointerEvent) => {
      if (animFrame !== null) return;
      
      const dx = e.clientX - panStartPos.current.x;
      const dy = e.clientY - panStartPos.current.y;
      
      animFrame = requestAnimationFrame(() => {
        setPanOffset({
          x: panStartScroll.current.x + dx,
          y: panStartScroll.current.y + dy
        });
        setIsFitToPage(false);
        animFrame = null;
      });
    };

    const handleGlobalPointerUp = () => {
      setIsPanning(false);
      if (animFrame !== null) cancelAnimationFrame(animFrame);
    };

    window.addEventListener('pointermove', handleGlobalPointerMove);
    window.addEventListener('pointerup', handleGlobalPointerUp);
    return () => {
      window.removeEventListener('pointermove', handleGlobalPointerMove);
      window.removeEventListener('pointerup', handleGlobalPointerUp);
    };
  }, [isPanning]);

  // Mouse Wheel Zoom Listener
  useEffect(() => {
    const container = stageContainerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
      setZoomLevel(prev => {
        const nextZoom = prev * zoomFactor;
        return Math.min(Math.max(0.1, Math.round(nextZoom * 100) / 100), 5.0);
      });
      setIsFitToPage(false);
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const handleToggleSkipDisableSelected = () => {
    if (selectedElementIds.length === 0) {
      triggerToast("Select an element on canvas to Skip/Disable");
      return;
    }
    const anyDisabled = selectedElements.some(el => el.disabled || el.isSkipped);
    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(el => {
        if (selectedElementIds.includes(el.id)) {
          return { ...el, disabled: !anyDisabled, isSkipped: !anyDisabled };
        }
        return el;
      })
    }));
    triggerToast(!anyDisabled ? "Selected element(s) Skipped/Disabled" : "Selected element(s) Enabled");
  };

  const [show3DProof, setShow3DProof] = useState(false);
  const [previewRecordIndex, setPreviewRecordIndex] = useState(0);
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [showGuides, setShowGuides] = useState(true);
  const [guides, setGuides] = useState<GuideLine[]>([
    { id: 'g1', type: 'horizontal', position: 30 },
    { id: 'g2', type: 'vertical', position: 30 }
  ]);
  const [mouseCanvasPos, setMouseCanvasPos] = useState<{ x: number; y: number } | null>(null);
  const [marqueeBox, setMarqueeBox] = useState<{ startX: number; startY: number; currentX: number; currentY: number } | null>(null);

  const handleAddGuide = (type: 'horizontal' | 'vertical', position: number) => {
    setGuides(prev => [...prev, { id: 'guide-' + Math.random().toString(36).substring(2, 9), type, position }]);
  };

  const handleUpdateGuide = (id: string, position: number) => {
    setGuides(prev => prev.map(g => g.id === id ? { ...g, position } : g));
  };

  const handleRemoveGuide = (id: string) => {
    setGuides(prev => prev.filter(g => g.id !== id));
  };

  const handleClearGuides = () => {
    setGuides([]);
  };

  const handleToggleGuides = () => {
    setShowGuides(prev => !prev);
  };
  const canvasRef = useRef<HTMLDivElement>(null);
  const spacePressedRef = useRef(false);
  const [zoomDrag, setZoomDrag] = useState<{ startY: number, startZoom: number } | null>(null);

  // Undo / Redo
  const [history, setHistory] = useState<VdpTemplate[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const skipHistoryRecord = useRef(false);

  // Clipboard & Context Menu
  const [clipboard, setClipboard] = useState<VdpElement[] | VdpElement | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number, y: number, elementId: string | null } | null>(null);

  // Drag and drop for layers
  const [draggedLayerId, setDraggedLayerId] = useState<string | null>(null);

  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [editingLayerId, setEditingLayerId] = useState<string | null>(null);
  const [sidebarTab, setSidebarTab] = useState<'properties' | 'layers'>('properties');
  const [isStickerSettingsExpanded, setIsStickerSettingsExpanded] = useState<boolean>(true);
  const [isBrandPaletteExpanded, setIsBrandPaletteExpanded] = useState<boolean>(true);
  const [isAddElementsExpanded, setIsAddElementsExpanded] = useState<boolean>(true);
  const [isSmartVariablesExpanded, setIsSmartVariablesExpanded] = useState<boolean>(true);
  const [newCustomVarName, setNewCustomVarName] = useState('');
  const [showAddVarInput, setShowAddVarInput] = useState(false);
  const [isPropertiesLayersExpanded, setIsPropertiesLayersExpanded] = useState<boolean>(true);
  const [showPropertiesModal, setShowPropertiesModal] = useState<boolean>(false);
  const [showDatePickerModal, setShowDatePickerModal] = useState<boolean>(false);

  useEffect(() => {
    const handleOpenDatePicker = () => {
      setShowDatePickerModal(true);
    };
    window.addEventListener('open-date-picker', handleOpenDatePicker);
    return () => {
      window.removeEventListener('open-date-picker', handleOpenDatePicker);
    };
  }, []);

  // Unified Pencil & Pen Vector Drawing Mode State
  const [drawingMode, setDrawingMode] = useState<'unified' | null>(null);
  const [subDrawTool, setSubDrawTool] = useState<'pencil' | 'pen'>('pen');
  const [drawingPoints, setDrawingPoints] = useState<Point[]>([]);
  const [isPenDraggingHandle, setIsPenDraggingHandle] = useState<boolean>(false);
  const drawingPointsRef = useRef<Point[]>([]);
  
  useEffect(() => {
    drawingPointsRef.current = drawingPoints;
  }, [drawingPoints]);

  const [drawSmooth, setDrawSmooth] = useState(true);
  const [drawClosedPath, setDrawClosedPath] = useState(false);
  const [drawFillColor, setDrawFillColor] = useState('none');
  const [drawStrokeColor, setDrawStrokeColor] = useState('#2563eb');
  const [drawStrokeWidth, setDrawStrokeWidth] = useState(3);

  const startManualDrawing = (mode: 'unified' = 'unified', defaultSubTool: 'pencil' | 'pen' = 'pen') => {
    setDrawingMode(mode);
    setSubDrawTool(defaultSubTool);
    setDrawingPoints([]);
    setSelectedElementIds([]);
    setDrawClosedPath(false);
    setDrawSmooth(true);
    setDrawFillColor('none');
    setDrawStrokeColor('#2563eb');
    setDrawStrokeWidth(3);
  };

  const finishManualDrawing = (pointsToUse: Point[] = drawingPoints) => {
    if (pointsToUse.length < 2) {
      toast.info('Drawing Tool', 'Please click or drag on the canvas to draw your stroke or vector shape.');
      return;
    }

    const allX: number[] = [];
    const allY: number[] = [];
    pointsToUse.forEach(p => {
      allX.push(p.x);
      allY.push(p.y);
      if (p.cp1) { allX.push(p.cp1.x); allY.push(p.cp1.y); }
      if (p.cp2) { allX.push(p.cp2.x); allY.push(p.cp2.y); }
    });

    const minX = Math.min(...allX);
    const maxX = Math.max(...allX);
    const minY = Math.min(...allY);
    const maxY = Math.max(...allY);

    const pad = Math.ceil(drawStrokeWidth / 2) + 8;
    const width = Math.max(16, maxX - minX + pad * 2);
    const height = Math.max(16, maxY - minY + pad * 2);

    const relPoints: Point[] = pointsToUse.map(p => ({
      ...p,
      x: Math.round(p.x - minX + pad),
      y: Math.round(p.y - minY + pad),
      cp1: p.cp1 ? { x: Math.round(p.cp1.x - minX + pad), y: Math.round(p.cp1.y - minY + pad) } : undefined,
      cp2: p.cp2 ? { x: Math.round(p.cp2.x - minX + pad), y: Math.round(p.cp2.y - minY + pad) } : undefined,
    }));

    const pathD = subDrawTool === 'pen'
      ? bezierPointsToSvgPath(relPoints, drawClosedPath)
      : pointsToSvgPath(relPoints, drawClosedPath, drawSmooth);

    const newElement: VdpElement = {
      id: Math.random().toString(36).substring(2, 9),
      type: subDrawTool === 'pen' ? 'pen' : 'path',
      x: Math.max(0, minX - pad),
      y: Math.max(0, minY - pad),
      width,
      height,
      pathData: pathD,
      pathD: pathD,
      points: relPoints,
      closedPath: drawClosedPath,
      isSmooth: subDrawTool === 'pen' ? false : drawSmooth,
      color: drawFillColor,
      borderColor: drawStrokeColor,
      strokeColor: drawStrokeColor,
      borderWidth: drawStrokeWidth,
      strokeWidth: drawStrokeWidth,
      columnName: '',
      fontSize: 14,
    };

    setTemplate(prev => ({
      ...prev,
      elements: [...prev.elements, newElement]
    }));
    setSelectedElementIds([newElement.id]);
    setDrawingMode(null);
    setDrawingPoints([]);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (activeCreationTool) {
        if (e.key === 'Escape') {
          setActiveCreationTool(null);
          setCreationPreviewBox(null);
        }
      }
      if (drawingMode) {
        if (e.key === 'Escape') {
          setDrawingMode(null);
          setDrawingPoints([]);
        } else if (e.key === 'Enter') {
          if (drawingPoints.length >= 2) {
            finishManualDrawing();
          }
        } else if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
          e.preventDefault();
          setDrawingPoints(pts => pts.slice(0, -1));
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [drawingMode, drawingPoints, drawClosedPath, drawSmooth, drawFillColor, drawStrokeColor, drawStrokeWidth]);

  // Auto sync CSV data on load if template has mapped elements but csvData is missing them
  useEffect(() => {
    if (template && template.elements && template.elements.some(el => !el.isStatic && el.columnName)) {
      const synced = autoSyncCsvForTemplate(template, csvData);
      const isDifferent = !csvData || 
        synced.headers.length !== csvData.headers.length || 
        synced.rows.length !== csvData.rows.length ||
        synced.headers.some(h => !csvData.headers.includes(h));
      if (isDifferent) {
        setCsvData(synced);
      }
    }
  }, []);

  useEffect(() => {
    if (!contextMenu) return;
    const handleClick = () => setContextMenu(null);
    window.addEventListener('pointerdown', handleClick);
    return () => window.removeEventListener('pointerdown', handleClick);
  }, [contextMenu]);

  useEffect(() => {
    if (skipHistoryRecord.current) {
      skipHistoryRecord.current = false;
      return;
    }
    // Skip recording transient intermediate frames during active dragging/resizing/rotating
    if (draggingId || resizingId || rotatingId) {
      return;
    }
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(template);
      if (newHistory.length > 100) newHistory.shift();
      setHistoryIndex(newHistory.length - 1);
      return newHistory;
    });
  }, [template, draggingId, resizingId, rotatingId]);

  const handleUndo = () => {
    if (historyIndex > 0) {
      skipHistoryRecord.current = true;
      setHistoryIndex(prev => prev - 1);
      setTemplate(history[historyIndex - 1]);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      skipHistoryRecord.current = true;
      setHistoryIndex(prev => prev + 1);
      setTemplate(history[historyIndex + 1]);
    }
  };

  const handleUpdateTemplateSize = (width: number, height: number) => {
    setTemplate({ ...template, width, height });
  };

  const handleAddElement = (type: ElementType) => {
    // Find the first unused column
    const usedColumns = new Set(template.elements.map(el => el.columnName));
    let availableColumn = csvData?.headers.find(h => !usedColumns.has(h));
    
    // Auto-create a column if none available
    if (!availableColumn && !['rectangle', 'circle', 'star', 'polygon', 'line', 'path', 'pen'].includes(type)) {
      const baseName = type.charAt(0).toUpperCase() + type.slice(1);
      let suffix = 1;
      let newColName = `${baseName}_${suffix}`;
      while (csvData?.headers?.includes(newColName)) {
        suffix++;
        newColName = `${baseName}_${suffix}`;
      }
      
      availableColumn = newColName;
      if (csvData) {
        setCsvData({
          headers: [...csvData.headers, newColName],
          rows: csvData.rows.map((row, i) => ({ ...row, [newColName]: `Sample ${type} ${i + 1}` }))
        });
      } else {
        setCsvData({
          headers: [newColName],
          rows: [{ [newColName]: `Sample ${type} 1` }, { [newColName]: `Sample ${type} 2` }, { [newColName]: `Sample ${type} 3` }]
        });
      }
    }

    const isShape = ['rectangle', 'circle', 'star', 'polygon', 'triangle', 'diamond', 'heart', 'arrow'].includes(type);
    const isBarcodeType = ['barcode', 'ean13', 'upca', 'pdf417'].includes(type);
    const isSquareType = ['qr', 'image', 'circle', 'star', 'polygon', 'triangle', 'diamond', 'heart', 'datamatrix', 'aztec'].includes(type);

    const newElement: VdpElement = {
      id: Math.random().toString(36).substring(2, 9),
      type,
      x: 15,
      y: 15,
      width: type === 'line' ? 120 : (type === 'textbox' ? 160 : (type === 'pdf417' ? 140 : (isSquareType ? 80 : (isBarcodeType ? 120 : 100)))),
      height: type === 'line' ? 24 : (type === 'textbox' ? 60 : (type === 'pdf417' ? 60 : (isSquareType ? 80 : (isBarcodeType ? 50 : (isShape ? 60 : 24))))),
      columnName: availableColumn || '',
      staticValue: type === 'textbox' ? 'Text Box (Double-click to edit)' : (type === 'aztec' ? 'AZTEC2026' : (type === 'pdf417' ? 'PDF417-BATCH' : (type === 'serial' ? '1' : (type === 'currency' ? '99.99' : (type === 'date' ? new Date().toISOString().split('T')[0] : (isBarcodeType ? '12345678' : `Sample ${type}`)))))),
      content: type === 'textbox' ? 'Text Box (Double-click to edit)' : undefined,
      text: type === 'textbox' ? 'Text Box (Double-click to edit)' : undefined,
      isTextBox: type === 'textbox',
      wrapText: type === 'textbox',
      fontSize: 14,
      fontFamily: 'Helvetica',
      color: type === 'star' ? '#f59e0b' : (type === 'heart' ? '#ec4899' : (type === 'polygon' || type === 'triangle' || type === 'diamond' || type === 'arrow' ? '#3b82f6' : (isShape ? '#e2e8f0' : '#000000'))),
      fillColor: type === 'star' ? '#f59e0b' : (type === 'heart' ? '#ec4899' : (type === 'polygon' || type === 'triangle' || type === 'diamond' || type === 'arrow' ? '#3b82f6' : (isShape ? '#e2e8f0' : '#000000'))),
      borderColor: type === 'star' ? '#d97706' : (type === 'heart' ? '#be185d' : (type === 'polygon' || type === 'triangle' || type === 'diamond' || type === 'arrow' ? '#1d4ed8' : (isShape ? '#94a3b8' : (type === 'line' ? '#000000' : undefined)))),
      strokeColor: type === 'star' ? '#d97706' : (type === 'heart' ? '#be185d' : (type === 'polygon' || type === 'triangle' || type === 'diamond' || type === 'arrow' ? '#1d4ed8' : (isShape ? '#94a3b8' : (type === 'line' ? '#000000' : undefined)))),
      borderWidth: isShape ? 1 : (type === 'line' ? 2 : undefined),
      strokeWidth: isShape ? 1 : (type === 'line' ? 2 : undefined),
      borderRadius: type === 'rectangle' ? 4 : 0,
      cornerRadius: type === 'rectangle' ? 4 : 0,
      lineCap: 'round',
      lineDash: 'solid',
      lineEffect: 'none',
      starPoints: 5,
      starInnerRadiusRatio: 0.4,
      polygonSides: 6,
      currencySymbol: '₹',
      dateFormat: 'DD/MM/YYYY',
      showBarcodeText: true,
      qrErrorCorrection: 'M',
      qrStyle: 'square',
      serialPrefix: 'SN-',
      serialStart: 1,
      serialPadding: 4,
      src: type === 'image' ? 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=300&q=80' : undefined,
      pathData: type === 'path' ? 'M 0 40 C 30 10, 70 70, 100 40' : undefined,
      points: type === 'pen' ? [{x: 10, y: 70}, {x: 40, y: 20}, {x: 70, y: 70}, {x: 90, y: 30}] : undefined,
      closedPath: false,
      isSmooth: false,
    };
    setTemplate(prev => ({ ...prev, elements: [...prev.elements, newElement] }));
    setSelectedElementIds([newElement.id]);
    if (type === 'textbox' || type === 'text') {
      setEditingTextId(newElement.id);
    }
  };

  const handleAddColumnElement = (colName: string, suggestedType: ElementType = 'text') => {
    const isSquareType = suggestedType === 'qr';
    const isBarcodeType = suggestedType === 'barcode' || suggestedType === 'ean13' || suggestedType === 'upca';

    const sampleVal = csvData?.rows?.[previewRecordIndex]?.[colName] || csvData?.rows?.[0]?.[colName] || `Sample ${colName}`;

    const newElement: VdpElement = {
      id: Math.random().toString(36).substring(2, 9),
      type: suggestedType,
      x: 20 + ((template.elements.length * 12) % 60),
      y: 20 + ((template.elements.length * 12) % 60),
      width: isSquareType ? 80 : (isBarcodeType ? 120 : 120),
      height: isSquareType ? 80 : (isBarcodeType ? 50 : 26),
      columnName: colName,
      staticValue: String(sampleVal),
      fontSize: 14,
      fontFamily: 'Helvetica',
      color: '#000000',
      fillColor: '#000000',
      currencySymbol: '₹',
      dateFormat: 'DD/MM/YYYY',
      showBarcodeText: true,
      qrErrorCorrection: 'M',
      qrStyle: 'square',
    };
    setTemplate(prev => ({ ...prev, elements: [...prev.elements, newElement] }));
    setSelectedElementIds([newElement.id]);
    triggerToast(`Added variable field: {{${colName}}}`);
  };

  const handleCreateCustomVariable = () => {
    const trimmed = newCustomVarName.trim().replace(/[^a-zA-Z0-9_-]/g, '_');
    if (!trimmed) return;
    if (csvData?.headers?.includes(trimmed)) {
      triggerToast(`Variable "${trimmed}" already exists`);
      return;
    }
    const defaultHeaders = csvData?.headers || [];
    const defaultRows = csvData?.rows && csvData.rows.length > 0 ? csvData.rows : [{}];
    const updatedHeaders = [...defaultHeaders, trimmed];
    const updatedRows = defaultRows.map((row, idx) => ({
      ...row,
      [trimmed]: `Sample ${trimmed} ${idx + 1}`
    }));
    setCsvData({ headers: updatedHeaders, rows: updatedRows });
    handleAddColumnElement(trimmed, 'text');
    setNewCustomVarName('');
    setShowAddVarInput(false);
  };

  const handleUpdateElement = useCallback((id: string, updates: Partial<VdpElement>) => {
    const normalized = normalizeUpdates(updates);
    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(el => el.id === id ? { ...el, ...normalized } : el)
    }));
  }, []);

  const handleUpdateSelectedElements = useCallback((updates: Partial<VdpElement>) => {
    if (selectedElementIdsRef.current.length === 0) return;
    const normalized = normalizeUpdates(updates);
    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(el => selectedElementIdsRef.current.includes(el.id) ? { ...el, ...normalized } : el)
    }));
  }, []);

  const handleUpdateTemplateProperties = useCallback((updates: Partial<VdpTemplate>) => {
    setTemplate(prev => ({ ...prev, ...updates }));
  }, []);
  
  const handleSelectElementById = useCallback((id: string) => {
    setSelectedElementIds([id]);
  }, []);

  const handleBatchUpdateElements = useCallback((updatesMap: Record<string, Partial<VdpElement>>) => {
    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(el => {
        if (updatesMap[el.id]) {
          return { ...el, ...normalizeUpdates(updatesMap[el.id]) };
        }
        return el;
      })
    }));
  }, []);

  const handleDeleteElement = (id: string | string[]) => {
    const rawIds = Array.isArray(id) ? id : [id];
    if (rawIds.length === 0) return;
    const idsToDelete = expandSelectionGroups(rawIds, template.elements);
    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.filter(el => !idsToDelete.includes(el.id))
    }));
    setSelectedElementIds(prev => prev.filter(i => !idsToDelete.includes(i)));
  };

  const handleDuplicateElement = (id: string | string[]) => {
    const idsToDuplicate = Array.isArray(id) ? id : [id];
    if (idsToDuplicate.length === 0) return;
    
    const allIdsToDuplicate = expandSelectionGroups(idsToDuplicate, template.elements);
    const newIds: string[] = [];
    const groupMapping: Record<string, string> = {};

    setTemplate(prev => {
      const newEls = prev.elements
        .filter(el => allIdsToDuplicate.includes(el.id))
        .map(el => {
          const newId = Date.now().toString() + Math.random().toString(36).substr(2, 4);
          newIds.push(newId);

          let newGroupId = el.groupId;
          if (el.groupId) {
            if (!groupMapping[el.groupId]) {
              groupMapping[el.groupId] = 'group-' + Math.random().toString(36).substring(2, 9);
            }
            newGroupId = groupMapping[el.groupId];
          }

          return { ...el, id: newId, groupId: newGroupId, x: el.x + 20, y: el.y + 20 };
        });
      return { ...prev, elements: [...prev.elements, ...newEls] };
    });
    if (newIds.length > 0) setSelectedElementIds(newIds);
  };

  const handleDuplicateSelected = useCallback(() => {
    handleDuplicateElement(selectedElementIdsRef.current);
  }, []);
  
  const handleDeleteSelected = useCallback(() => {
    handleDeleteElement(selectedElementIdsRef.current);
  }, []);

  const handleMoveSelectedLayerUp = useCallback(() => {
    selectedElementIdsRef.current.forEach(id => handleMoveLayer(id, 'up'));
  }, []);

  const handleMoveSelectedLayerDown = useCallback(() => {
    selectedElementIdsRef.current.forEach(id => handleMoveLayer(id, 'down'));
  }, []);

  const handleToggleLockSelected = useCallback(() => {
    const ids = selectedElementIdsRef.current;
    if (ids.length === 0) return;
    setTemplate(prev => {
      const selected = prev.elements.filter(e => ids.includes(e.id));
      const allLocked = selected.every(e => e.locked);
      return {
        ...prev,
        elements: prev.elements.map(e => ids.includes(e.id) ? { ...e, locked: !allLocked } : e)
      };
    });
  }, []);

  const handleTogglePreview = useCallback(() => {
    setIsPreviewCanvasActive(prev => !prev);
  }, []);

  const handleToggleHandTool = useCallback(() => {
    setIsHandToolActive(prev => !prev);
  }, []);

  const handleOpenLivePreviewModal = useCallback(() => {
    setShowLivePreviewModal(true);
  }, []);

  const handleGroupSelectedElements = useCallback(() => {
    if (selectedElementIds.length < 2) {
      triggerToast("Select at least 2 elements to group");
      return;
    }
    const newGroupId = 'group-' + Math.random().toString(36).substring(2, 9);
    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(el => {
        if (selectedElementIds.includes(el.id)) {
          return { ...el, groupId: newGroupId };
        }
        return el;
      })
    }));
    triggerToast("Selected elements grouped");
  }, [selectedElementIds, triggerToast]);

  const handleUngroupSelectedElements = useCallback(() => {
    if (selectedElementIds.length === 0) return;
    const selectedEls = template.elements.filter(el => selectedElementIds.includes(el.id));
    const groupIdsToDissolve = Array.from(new Set(selectedEls.map(el => el.groupId).filter(Boolean)));
    
    if (groupIdsToDissolve.length === 0) {
      triggerToast("No grouped elements selected");
      return;
    }

    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(el => {
        if (el.groupId && groupIdsToDissolve.includes(el.groupId)) {
          const { groupId, ...rest } = el;
          return rest;
        }
        return el;
      })
    }));
    triggerToast("Elements ungrouped");
  }, [selectedElementIds, template.elements, triggerToast]);

  const handleMoveLayer = (id: string, direction: 'up' | 'down' | 'top' | 'bottom') => {
    setTemplate(prev => {
      const idx = prev.elements.findIndex(e => e.id === id);
      if (idx === -1) return prev;
      const elements = [...prev.elements];
      const [item] = elements.splice(idx, 1);
      if (direction === 'up') {
        elements.splice(Math.min(elements.length, idx + 1), 0, item);
      } else if (direction === 'down') {
        elements.splice(Math.max(0, idx - 1), 0, item);
      } else if (direction === 'top') {
        elements.push(item);
      } else if (direction === 'bottom') {
        elements.unshift(item);
      }
      return { ...prev, elements };
    });
  };

  const handleApplyPathfinder = (mode: 'unite' | 'subtract' | 'intersect' | 'exclude' | 'divide' | 'merge') => {
    const targets = selectedElements.length >= 2 ? selectedElements : panelSelectedElements;
    if (targets.length < 2) {
      toast.info('Pathfinder Tool', 'Please select at least 2 shapes on canvas to apply Pathfinder.');
      return;
    }
    const updated = applyPathfinderOperation(targets, template.elements, mode);
    if (updated === template.elements) {
      toast.info('Pathfinder Tool', `No overlapping geometry found for ${mode.toUpperCase()}. Ensure shapes overlap.`);
      return;
    }
    setTemplate(prev => ({ ...prev, elements: updated }));
    const newEl = updated.find(e => e.id.startsWith('pathfinder-'));
    if (newEl) {
      setSelectedElementIds([newEl.id]);
    }
    const modeLabels: Record<string, string> = {
      unite: 'Union (Merged Shapes)',
      subtract: 'Subtract (Minus Front)',
      intersect: 'Intersect (Overlap Region)',
      exclude: 'Exclude (XOR Cutout)',
      divide: 'Divide',
      merge: 'Merge'
    };
    setToastNotification(`Pathfinder: ${modeLabels[mode] || mode.toUpperCase()}`);
    setTimeout(() => setToastNotification(null), 2500);
  };

  const handleOpenSimplifyPath = useCallback(() => {
    if (selectedElementIds.length === 0) {
      const firstVector = template.elements.find(el =>
        el.type === 'pen' || el.type === 'path' || Boolean(el.points && el.points.length > 0) || Boolean(el.pathData || el.pathD)
      );
      if (firstVector) {
        setSelectedElementIds([firstVector.id]);
        setShowSimplifyPathModal(true);
        return;
      }
      toast.info('Simplify Path Tool', 'Select a vector path, pencil stroke, or shape on canvas to simplify.');
      return;
    }

    const hasVectorSelected = selectedElementIds.some(id => {
      const el = template.elements.find(e => e.id === id);
      return el && (
        el.type === 'pen' || 
        el.type === 'path' || 
        Boolean(el.points && el.points.length > 0) || 
        Boolean(el.pathData || el.pathD) ||
        ['star', 'polygon', 'rectangle', 'circle', 'triangle', 'diamond', 'heart', 'arrow'].includes(el.type)
      );
    });

    if (!hasVectorSelected) {
      toast.info('Simplify Path Tool', 'Selected element is not a vector path. Select a vector path, pencil stroke, or shape.');
      return;
    }

    setShowSimplifyPathModal(true);
  }, [selectedElementIds, template.elements, toast]);

  const handleSimplifyPreviewUpdate = useCallback((preview: VdpElement[] | null) => {
    setSimplifyPreviewElements(preview);
  }, []);

  const handleApplySimplifyPath = useCallback((
    updatedElements: VdpElement[],
    summary: { totalOriginal: number; totalSimplified: number; overallReduction: number }
  ) => {
    const updatedMap = new Map(updatedElements.map(e => [e.id, e]));
    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(el => updatedMap.get(el.id) || el)
    }));
    setSimplifyPreviewElements(null);

    try {
      playfulAudio.playCutePop(1.2);
    } catch {}

    triggerToast(`Simplify Path: Reduced from ${summary.totalOriginal} to ${summary.totalSimplified} nodes (-${summary.overallReduction}%)`);
    setToastNotification(`Path Simplified: -${summary.overallReduction}% points`);
    setTimeout(() => setToastNotification(null), 2500);
  }, [triggerToast]);

  const displayElements = useMemo(() => {
    if (!simplifyPreviewElements || simplifyPreviewElements.length === 0) {
      return template.elements;
    }
    const previewMap = new Map(simplifyPreviewElements.map(e => [e.id, e]));
    return template.elements.map(el => previewMap.get(el.id) || el);
  }, [template.elements, simplifyPreviewElements]);

  const handleContextMenu = useCallback((e: React.MouseEvent, elementId: string | null) => {
    // Allow default browser context menu for inputs and textareas
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    if (elementId) {
      setSelectedElementIds(prev => {
        if (prev.includes(elementId)) {
          return prev; // Keep multi-selection intact!
        }
        return [elementId];
      });
    }
    setContextMenu({ x: e.clientX, y: e.clientY, elementId });
  }, []);

  const closeContextMenu = () => setContextMenu(null);

  const handleCopyElement = (id: string) => {
    const el = template.elements.find(e => e.id === id);
    if (el) setClipboard(el);
    closeContextMenu();
  };

  const handleNewDocument = () => {
    setTemplate(prev => ({
      ...prev,
      elements: []
    }));
    setSelectedElementIds([]);
    triggerToast("New blank document created.");
  };

  const handleQuickSave = () => {
    setShowSaveModeInManager(true);
    setShowTemplateManager(true);
  };

  const handleCutElement = (id: string) => {
    const el = template.elements.find(e => e.id === id);
    if (el) {
      setClipboard(el);
      handleDeleteElement(id);
    }
    closeContextMenu();
  };

  const handlePasteElement = () => {
    if (clipboard) {
      const clipArr = Array.isArray(clipboard) ? clipboard : [clipboard];
      if (clipArr.length > 0) {
        const newIds: string[] = [];
        const newEls = clipArr.map(item => {
          const newId = Date.now().toString() + Math.random().toString(36).substr(2, 4);
          newIds.push(newId);
          return { ...item, id: newId, x: item.x + 20, y: item.y + 20 };
        });
        setTemplate(prev => ({ ...prev, elements: [...prev.elements, ...newEls] }));
        setSelectedElementIds(newIds);
      }
    }
    closeContextMenu();
  };

  const handleAnchorPointDrag = useCallback((e: React.PointerEvent, el: VdpElement, pointIdx: number) => {
    e.stopPropagation();
    const targetEl = e.target as HTMLElement;
    try { targetEl.setPointerCapture(e.pointerId); } catch {}
    if (!canvasRef.current || !el.points) return;
    const startX = e.clientX;
    const startY = e.clientY;
    let origPt = { ...el.points[pointIdx] };
    let hasSyncedHandles = false;

    const handlePointerMove = (ev: PointerEvent) => {
      const dx = (ev.clientX - startX) / zoomLevel;
      const dy = (ev.clientY - startY) / zoomLevel;
      setTemplate(prev => ({
        ...prev,
        elements: prev.elements.map(item => {
          if (item.id === el.id && item.points) {
            const currentItemPt = item.points[pointIdx];
            if (!hasSyncedHandles && currentItemPt) {
              if (!origPt.cp1 && currentItemPt.cp1) origPt.cp1 = { ...currentItemPt.cp1 };
              if (!origPt.cp2 && currentItemPt.cp2) origPt.cp2 = { ...currentItemPt.cp2 };
              hasSyncedHandles = true;
            }
            const updatedPts = [...item.points];
            const pt = { ...origPt };
            pt.x = Math.round(origPt.x + dx);
            pt.y = Math.round(origPt.y + dy);
            if (origPt.cp1) {
              pt.cp1 = { x: Math.round(origPt.cp1.x + dx), y: Math.round(origPt.cp1.y + dy) };
            }
            if (origPt.cp2) {
              pt.cp2 = { x: Math.round(origPt.cp2.x + dx), y: Math.round(origPt.cp2.y + dy) };
            }
            updatedPts[pointIdx] = pt;
            const newPathD = (item.type === 'pen' || item.type === 'path' || updatedPts.some(p => p.cp1 || p.cp2))
              ? bezierPointsToSvgPath(updatedPts, item.closedPath)
              : pointsToSvgPath(updatedPts, item.closedPath, item.isSmooth);
            return {
              ...item,
              points: updatedPts,
              pathData: newPathD,
              pathD: newPathD
            };
          }
          return item;
        })
      }));
    };

    const handlePointerUp = (upEv: PointerEvent) => {
      try { targetEl.releasePointerCapture(upEv.pointerId); } catch {}
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  }, [zoomLevel]);

  const handleControlPointDrag = useCallback((e: React.PointerEvent, el: VdpElement, pointIdx: number, type: 'cp1' | 'cp2') => {
    e.stopPropagation();
    const targetEl = e.target as HTMLElement;
    try { targetEl.setPointerCapture(e.pointerId); } catch {}
    if (!canvasRef.current || !el.points) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const curPt = el.points[pointIdx];
    const { dirX, dirY } = getAnchorTangent(el.points, pointIdx, el.closedPath, 28);
    const startBaseCp1 = curPt?.cp1 ? { ...curPt.cp1 } : { x: curPt.x - dirX, y: curPt.y - dirY };
    const startBaseCp2 = curPt?.cp2 ? { ...curPt.cp2 } : { x: curPt.x + dirX, y: curPt.y + dirY };

    const handlePointerMove = (ev: PointerEvent) => {
      const dx = (ev.clientX - startX) / zoomLevel;
      const dy = (ev.clientY - startY) / zoomLevel;
      setTemplate(prev => ({
        ...prev,
        elements: prev.elements.map(item => {
          if (item.id === el.id && item.points) {
            const updatedPts = item.points.map(p => ({ ...p }));
            const pt = { ...updatedPts[pointIdx] };
            if (type === 'cp1') {
              pt.cp1 = { x: Math.round(startBaseCp1.x + dx), y: Math.round(startBaseCp1.y + dy) };
              if (!pt.cp2) pt.cp2 = startBaseCp2;
            } else if (type === 'cp2') {
              pt.cp2 = { x: Math.round(startBaseCp2.x + dx), y: Math.round(startBaseCp2.y + dy) };
              if (!pt.cp1) pt.cp1 = startBaseCp1;
            }
            pt.type = 'smooth';
            updatedPts[pointIdx] = pt;
            const newPathD = bezierPointsToSvgPath(updatedPts, item.closedPath);
            return {
              ...item,
              points: updatedPts,
              pathData: newPathD,
              pathD: newPathD
            };
          }
          return item;
        })
      }));
    };

    const handlePointerUp = (upEv: PointerEvent) => {
      try { targetEl.releasePointerCapture(upEv.pointerId); } catch {}
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  }, [zoomLevel]);

  const handleEnsureAnchorHandles = useCallback((el: VdpElement, pointIdx: number) => {
    if (!el.points) return;
    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(item => {
        if (item.id === el.id && item.points) {
          const pt = item.points[pointIdx];
          if (pt && pt.cp1 && pt.cp2) return item;
          const updatedPts = item.points.map(p => ({ ...p }));
          const curPt = { ...updatedPts[pointIdx] };
          const { dirX, dirY } = getAnchorTangent(updatedPts, pointIdx, item.closedPath, 28);
          if (!curPt.cp1) {
            curPt.cp1 = { x: Math.round(curPt.x - dirX), y: Math.round(curPt.y - dirY) };
          }
          if (!curPt.cp2) {
            curPt.cp2 = { x: Math.round(curPt.x + dirX), y: Math.round(curPt.y + dirY) };
          }
          curPt.type = 'smooth';
          updatedPts[pointIdx] = curPt;
          const newPathD = bezierPointsToSvgPath(updatedPts, item.closedPath);
          return {
            ...item,
            points: updatedPts,
            pathData: newPathD,
            pathD: newPathD
          };
        }
        return item;
      })
    }));
  }, []);

  const handleToggleAnchorPointType = useCallback((el: VdpElement, pointIdx: number) => {
    if (!el.points) return;
    setTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(item => {
        if (item.id === el.id && item.points) {
          const updatedPts = item.points.map(p => ({ ...p }));
          const pt = { ...updatedPts[pointIdx] };
          if (pt.cp1 || pt.cp2) {
            delete pt.cp1;
            delete pt.cp2;
            pt.type = 'corner';
          } else {
            const { dirX, dirY } = getAnchorTangent(updatedPts, pointIdx, item.closedPath, 28);
            pt.cp1 = { x: Math.round(pt.x - dirX), y: Math.round(pt.y - dirY) };
            pt.cp2 = { x: Math.round(pt.x + dirX), y: Math.round(pt.y + dirY) };
            pt.type = 'smooth';
          }
          updatedPts[pointIdx] = pt;
          const newPathD = bezierPointsToSvgPath(updatedPts, item.closedPath);
          return {
            ...item,
            points: updatedPts,
            pathData: newPathD,
            pathD: newPathD
          };
        }
        return item;
      })
    }));
  }, []);

  const handleResizeStart = useCallback((e: React.PointerEvent, el: VdpElement, handle: string = 'se') => {
    e.stopPropagation();
    if (el.locked) return;
    const canvasRect = canvasRef.current?.getBoundingClientRect();
    if (!canvasRect) return;
    
    setResizingId(el.id);
    setResizeStart({
      startX: e.clientX,
      startY: e.clientY,
      width: el.width || 100,
      height: el.height || 100,
      x: el.x,
      y: el.y,
      fontSize: el.fontSize || 14,
      horizontalScale: el.horizontalScale !== undefined ? el.horizontalScale : 100,
      verticalScale: el.verticalScale !== undefined ? el.verticalScale : 100,
      handle
    });
  }, []);

  const handleRotateStart = useCallback((e: React.PointerEvent, el: VdpElement) => {
    e.stopPropagation();
    if (el.locked) return;
    
    const canvasRect = canvasRef.current?.getBoundingClientRect();
    if (!canvasRect) return;
    
    // Calculate center of element relative to screen
    const elNode = document.getElementById(`el-${el.id}`);
    if (!elNode) return;
    const rect = elNode.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const startAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);
    
    setRotatingId(el.id);
    setRotateStart({
      startAngle,
      initialRotation: el.rotation || 0,
      centerX,
      centerY
    });
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent, el: VdpElement) => {
    if (isHandToolActive || e.button === 1 || e.button === 2) return;
    
    const groupMemberIds = getGroupElementIds(el.id, templateRef.current.elements);
    const currentSelectedIds = selectedElementIdsRef.current;

    if (e.shiftKey) {
      const isAnySelected = groupMemberIds.some(id => currentSelectedIds.includes(id));
      if (isAnySelected) {
        setSelectedElementIds(prev => prev.filter(id => !groupMemberIds.includes(id)));
      } else {
        setSelectedElementIds(prev => Array.from(new Set([...prev, ...groupMemberIds])));
      }
    } else if (!currentSelectedIds.includes(el.id)) {
      setSelectedElementIds(groupMemberIds);
    }
    if (e.ctrlKey && spacePressedRef.current) return;
    
    e.stopPropagation();
    if (el.locked) return;
    
    const canvasRect = canvasRef.current?.getBoundingClientRect();
    if (!canvasRect) return;
    
    setDragOffset({
      x: (e.clientX - canvasRect.left) / zoomLevel - el.x,
      y: (e.clientY - canvasRect.top) / zoomLevel - el.y
    });
    setDraggingId(el.id);
  }, [isHandToolActive, zoomLevel]);

  const handleElementDoubleClick = useCallback((e: React.MouseEvent, el: VdpElement) => {
    e.stopPropagation();
    setSelectedElementIds([el.id]);
    
    if (['text', 'uppercase'].includes(el.type)) {
      setEditingTextId(el.id);
      return;
    }
    
    if (el.type === 'image') {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = (evt: any) => {
        const file = evt.target.files?.[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = (readerEvt) => {
            if (readerEvt.target?.result) {
              handleUpdateElement(el.id, { src: readerEvt.target.result as string, staticValue: readerEvt.target.result as string });
            }
          };
          reader.readAsDataURL(file);
        }
      };
      input.click();
    } else if (['text', 'uppercase', 'currency', 'date', 'serial'].includes(el.type)) {
      const newVal = window.prompt("Edit Text Value:", el.staticValue || el.text || '');
      if (newVal !== null) {
        handleUpdateElement(el.id, { staticValue: newVal, text: newVal });
      }
    } else {
      setShowPropertiesModal(true);
    }
  }, [handleUpdateElement]);

  // Resize & Rotate Effect with rAF throttling
  useEffect(() => {
    let animFrameId: number | null = null;

    const handlePointerMove = (e: PointerEvent) => {
      if (!resizingId && !rotatingId) return;
      if (animFrameId !== null) return;

      animFrameId = requestAnimationFrame(() => {
        animFrameId = null;
        if (resizingId && resizeStart) {
          const dx = (e.clientX - resizeStart.startX) / zoomLevel;
          const dy = (e.clientY - resizeStart.startY) / zoomLevel;
          const h = resizeStart.handle || 'se';
          
          setTemplate(prev => ({
            ...prev,
            elements: prev.elements.map(el => {
              if (el.id === resizingId) {
                if (['text', 'uppercase', 'currency', 'date', 'serial'].includes(el.type) && !el.isTextBox && el.type !== 'textbox') {
                  let delta = Math.max(dx, dy);
                  if (h.includes('w') || h.includes('n')) delta = -delta;
                  
                  // In typical vector tools, resizing text without a text box scales the font size directly.
                  // We also update width/height so the bounding box wraps it.
                  
                  let newWidth = resizeStart.width;
                  let newHeight = resizeStart.height;
                  
                  if (h.includes('e')) newWidth = Math.max(10, resizeStart.width + dx);
                  if (h.includes('w')) newWidth = Math.max(10, resizeStart.width - dx);
                  if (h.includes('s')) newHeight = Math.max(10, resizeStart.height + dy);
                  if (h.includes('n')) newHeight = Math.max(10, resizeStart.height - dy);
                  
                  // Corner drags should scale proportionally. We will use the width change as primary driver for scale.
                  const ratio = newWidth / resizeStart.width;
                  const newFontSize = Math.max(6, Math.round(resizeStart.fontSize * ratio));
                  
                  let newX = resizeStart.x;
                  let newY = resizeStart.y;
                  if (h.includes('w')) newX = resizeStart.x + (resizeStart.width - newWidth);
                  if (h.includes('n')) newY = resizeStart.y + (resizeStart.height - newHeight);
                  
                  // We also scale height proportionally to maintain tight bounding box.
                  const finalHeight = Math.round(resizeStart.height * ratio);
                  if (h.includes('n')) newY = resizeStart.y + (resizeStart.height - finalHeight);
                  
                  return { 
                    ...el, 
                    fontSize: newFontSize,
                    width: newWidth,
                    height: finalHeight,
                    x: newX,
                    y: newY
                  };
                } else {
                  let newWidth = resizeStart.width;
                  let newHeight = resizeStart.height;
                  let newX = resizeStart.x !== undefined ? resizeStart.x : el.x;
                  let newY = resizeStart.y !== undefined ? resizeStart.y : el.y;

                  if (h.includes('e')) {
                    newWidth = Math.max(10, Math.round(resizeStart.width + dx));
                  } else if (h.includes('w')) {
                    const w = Math.max(10, Math.round(resizeStart.width - dx));
                    newX = Math.round(resizeStart.x + (resizeStart.width - w));
                    newWidth = w;
                  }

                  if (h.includes('s')) {
                    newHeight = Math.max(10, Math.round(resizeStart.height + dy));
                  } else if (h.includes('n')) {
                    const hVal = Math.max(10, Math.round(resizeStart.height - dy));
                    newY = Math.round(resizeStart.y + (resizeStart.height - hVal));
                    newHeight = hVal;
                  }
                  
                  // Keep aspect ratio for standard handles when SHIFT is pressed
                  if (e.shiftKey && (h === 'nw' || h === 'ne' || h === 'sw' || h === 'se')) {
                    const aspect = resizeStart.width / resizeStart.height;
                    if (newWidth / newHeight > aspect) {
                      newWidth = Math.round(newHeight * aspect);
                      if (h.includes('w')) newX = resizeStart.x + resizeStart.width - newWidth;
                    } else {
                      newHeight = Math.round(newWidth / aspect);
                      if (h.includes('n')) newY = resizeStart.y + resizeStart.height - newHeight;
                    }
                  }

                  return { 
                    ...el, 
                    x: newX,
                    y: newY,
                    width: newWidth,
                    height: newHeight
                  };
                }
              }
              return el;
            })
          }));
        } else if (rotatingId && rotateStart) {
          const angle = Math.atan2(e.clientY - rotateStart.centerY, e.clientX - rotateStart.centerX) * (180 / Math.PI);
          let rotation = Math.round(rotateStart.initialRotation + (angle - rotateStart.startAngle));
          // snap to 45 deg if shift is held
          if (e.shiftKey) {
            rotation = Math.round(rotation / 45) * 45;
          }
          setTemplate(prev => ({
            ...prev,
            elements: prev.elements.map(el => el.id === rotatingId ? { ...el, rotation } : el)
          }));
        }
      });
    };
    
    const handlePointerUp = () => {
      if (animFrameId !== null) {
        cancelAnimationFrame(animFrameId);
        animFrameId = null;
      }
      setResizingId(null);
      setRotatingId(null);
    };
    
    if (resizingId || rotatingId) {
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    }
    return () => {
      if (animFrameId !== null) {
        cancelAnimationFrame(animFrameId);
      }
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [resizingId, resizeStart, rotatingId, rotateStart, zoomLevel]);

  // Dragging Effect with rAF throttling
  useEffect(() => {
    let animFrameId: number | null = null;

    const handlePointerMove = (e: PointerEvent) => {
      if (!draggingId) return;
      if (animFrameId !== null) return;

      animFrameId = requestAnimationFrame(() => {
        animFrameId = null;
        const canvasRect = canvasRef.current?.getBoundingClientRect();
        if (!canvasRect) return;
        
        let newX = Math.round((e.clientX - canvasRect.left) / zoomLevel - dragOffset.x);
        let newY = Math.round((e.clientY - canvasRect.top) / zoomLevel - dragOffset.y);
        
        // Grid Snapping (snap to 10px grid if snapToGrid is true, inverted by Shift key)
        const shouldSnap = snapToGrid ? !e.shiftKey : e.shiftKey;
        if (shouldSnap) {
          newX = Math.round(newX / 10) * 10;
          newY = Math.round(newY / 10) * 10;
        }

        // Guide Snapping: magnetic snap to active guide lines within 6px
        if (showGuides && guides.length > 0) {
          guides.forEach(g => {
            if (g.type === 'vertical') {
              if (Math.abs(newX - g.position) < 6) {
                newX = g.position;
              }
            } else if (g.type === 'horizontal') {
              if (Math.abs(newY - g.position) < 6) {
                newY = g.position;
              }
            }
          });
        }
        
        setTemplate(prev => {
          const draggedEl = prev.elements.find(item => item.id === draggingId);
          if (!draggedEl) return prev;

          const dx = newX - draggedEl.x;
          const dy = newY - draggedEl.y;

          if (dx === 0 && dy === 0) return prev;

          return {
            ...prev,
            elements: prev.elements.map(el => {
              if (selectedElementIds.includes(el.id) && !el.locked) {
                return { ...el, x: el.x + dx, y: el.y + dy };
              }
              return el;
            })
          };
        });
      });
    };
    
    const handlePointerUp = () => {
      if (animFrameId !== null) {
        cancelAnimationFrame(animFrameId);
        animFrameId = null;
      }
      setDraggingId(null);
    };

    if (draggingId) {
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    }

    return () => {
      if (animFrameId !== null) {
        cancelAnimationFrame(animFrameId);
      }
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [draggingId, dragOffset, zoomLevel, snapToGrid, showGuides, guides, selectedElementIds]);

  // Marquee Selection Box & Vector Drawing Handler for Canvas Dragging
  const handleCanvasPointerDown = (e: React.PointerEvent) => {
    if (e.button === 1) return; // Middle-click is reserved for canvas panning
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.round((e.clientX - rect.left) / zoomLevel);
    const y = Math.round((e.clientY - rect.top) / zoomLevel);

    if (activeCreationTool) {
      e.preventDefault();
      e.stopPropagation();
      const startX = x;
      const startY = y;
      setCreationPreviewBox({ startX, startY, currentX: startX, currentY: startY });

      const handlePointerMove = (ev: PointerEvent) => {
        if (!canvasRef.current) return;
        const currentRect = canvasRef.current.getBoundingClientRect();
        const currX = Math.round((ev.clientX - currentRect.left) / zoomLevel);
        const currY = Math.round((ev.clientY - currentRect.top) / zoomLevel);
        setCreationPreviewBox({ startX, startY, currentX: currX, currentY: currY });
      };

      const handlePointerUp = (upEv: PointerEvent) => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
        setCreationPreviewBox(null);

        if (!canvasRef.current) return;
        const currentRect = canvasRef.current.getBoundingClientRect();
        const endX = Math.round((upEv.clientX - currentRect.left) / zoomLevel);
        const endY = Math.round((upEv.clientY - currentRect.top) / zoomLevel);

        const rawW = Math.abs(endX - startX);
        const rawH = Math.abs(endY - startY);
        const isClick = rawW < 6 && rawH < 6;

        let finalX = Math.min(startX, endX);
        let finalY = Math.min(startY, endY);
        let finalW = rawW;
        let finalH = rawH;

        if (isClick) {
          finalX = startX;
          finalY = startY;
          if (activeCreationTool === 'textbox') { finalW = 160; finalH = 60; }
          else if (activeCreationTool === 'text') { finalW = 120; finalH = 26; }
          else if (activeCreationTool === 'rectangle') { finalW = 120; finalH = 80; }
          else if (activeCreationTool === 'circle') { finalW = 80; finalH = 80; }
          else if (activeCreationTool === 'star') { finalW = 90; finalH = 90; }
          else if (activeCreationTool === 'polygon') { finalW = 90; finalH = 90; }
          else if (activeCreationTool === 'line') { finalW = 120; finalH = 24; }
          else { finalW = 100; finalH = 60; }
        } else {
          finalW = Math.max(16, finalW);
          finalH = Math.max(16, finalH);
        }

        const currentTool = activeCreationTool;
        setActiveCreationTool(null);

        const newId = Math.random().toString(36).substring(2, 9);
        let newElement: VdpElement;

        if (currentTool === 'textbox') {
          newElement = {
            id: newId,
            type: 'textbox',
            isTextBox: true,
            wrapText: true,
            x: finalX,
            y: finalY,
            width: finalW,
            height: finalH,
            staticValue: 'Text Box (Double click to type)',
            content: 'Text Box (Double click to type)',
            text: 'Text Box (Double click to type)',
            fontSize: 14,
            fontFamily: 'Helvetica',
            color: '#000000',
            fillColor: '#000000',
            isStatic: true,
            columnName: '',
            lineHeight: 1.3,
            textAlign: 'left',
          };
        } else if (currentTool === 'text') {
          newElement = {
            id: newId,
            type: 'text',
            x: finalX,
            y: finalY,
            width: finalW,
            height: Math.max(20, finalH),
            staticValue: 'Sample Text',
            content: 'Sample Text',
            text: 'Sample Text',
            fontSize: 14,
            fontFamily: 'Helvetica',
            color: '#000000',
            fillColor: '#000000',
            isStatic: true,
            columnName: '',
            textAlign: 'left',
          };
        } else if (currentTool === 'rectangle') {
          newElement = {
            id: newId,
            type: 'rectangle',
            x: finalX,
            y: finalY,
            width: finalW,
            height: finalH,
            color: '#e2e8f0',
            fillColor: '#e2e8f0',
            borderColor: '#94a3b8',
            strokeColor: '#94a3b8',
            borderWidth: 1,
            strokeWidth: 1,
            borderRadius: 4,
            cornerRadius: 4,
            columnName: '',
          };
        } else if (currentTool === 'circle') {
          newElement = {
            id: newId,
            type: 'circle',
            x: finalX,
            y: finalY,
            width: finalW,
            height: finalH,
            color: '#e2e8f0',
            fillColor: '#e2e8f0',
            borderColor: '#94a3b8',
            strokeColor: '#94a3b8',
            borderWidth: 1,
            strokeWidth: 1,
            columnName: '',
          };
        } else if (currentTool === 'star') {
          newElement = {
            id: newId,
            type: 'star',
            x: finalX,
            y: finalY,
            width: finalW,
            height: finalH,
            color: '#f59e0b',
            fillColor: '#f59e0b',
            borderColor: '#d97706',
            strokeColor: '#d97706',
            borderWidth: 1,
            strokeWidth: 1,
            starPoints: 5,
            starInnerRadiusRatio: 0.4,
            columnName: '',
          };
        } else if (currentTool === 'polygon') {
          newElement = {
            id: newId,
            type: 'polygon',
            x: finalX,
            y: finalY,
            width: finalW,
            height: finalH,
            color: '#3b82f6',
            fillColor: '#3b82f6',
            borderColor: '#1d4ed8',
            strokeColor: '#1d4ed8',
            borderWidth: 1,
            strokeWidth: 1,
            polygonSides: 6,
            columnName: '',
          };
        } else if (currentTool === 'triangle') {
          newElement = {
            id: newId,
            type: 'triangle',
            x: finalX,
            y: finalY,
            width: finalW,
            height: finalH,
            color: '#3b82f6',
            fillColor: '#3b82f6',
            borderColor: '#1d4ed8',
            strokeColor: '#1d4ed8',
            borderWidth: 1,
            strokeWidth: 1,
            columnName: '',
          };
        } else if (currentTool === 'diamond') {
          newElement = {
            id: newId,
            type: 'diamond',
            x: finalX,
            y: finalY,
            width: finalW,
            height: finalH,
            color: '#3b82f6',
            fillColor: '#3b82f6',
            borderColor: '#1d4ed8',
            strokeColor: '#1d4ed8',
            borderWidth: 1,
            strokeWidth: 1,
            columnName: '',
          };
        } else if (currentTool === 'heart') {
          newElement = {
            id: newId,
            type: 'heart',
            x: finalX,
            y: finalY,
            width: finalW,
            height: finalH,
            color: '#ec4899',
            fillColor: '#ec4899',
            borderColor: '#be185d',
            strokeColor: '#be185d',
            borderWidth: 1,
            strokeWidth: 1,
            columnName: '',
          };
        } else if (currentTool === 'arrow') {
          newElement = {
            id: newId,
            type: 'arrow',
            x: finalX,
            y: finalY,
            width: finalW,
            height: finalH,
            color: '#3b82f6',
            fillColor: '#3b82f6',
            borderColor: '#1d4ed8',
            strokeColor: '#1d4ed8',
            borderWidth: 1,
            strokeWidth: 1,
            columnName: '',
          };
        } else if (currentTool === 'line') {
          newElement = {
            id: newId,
            type: 'line',
            x: finalX,
            y: finalY,
            width: Math.max(30, finalW),
            height: Math.max(16, finalH),
            borderColor: '#000000',
            strokeColor: '#000000',
            borderWidth: 2,
            strokeWidth: 2,
            lineCap: 'round',
            lineDash: 'solid',
            lineEffect: 'none',
            columnName: '',
          };
        } else {
          return;
        }

        setTemplate(prev => ({
          ...prev,
          elements: [...prev.elements, newElement]
        }));
        setSelectedElementIds([newElement.id]);

        if (currentTool === 'textbox' || currentTool === 'text') {
          setEditingTextId(newElement.id);
        }
      };

      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
      return;
    }

    if (drawingMode) {
      e.preventDefault();
      e.stopPropagation();

      if (subDrawTool === 'pen') {
        // Photoshop / Illustrator Pen Tool Drawing Logic
        const lockedX = Math.round(x);
        const lockedY = Math.round(y);

        if (drawingPoints.length > 2) {
          const p0 = drawingPoints[0];
          const distToStart = Math.hypot(lockedX - p0.x, lockedY - p0.y);
          if (distToStart < 14) {
            setDrawClosedPath(true);
            setTimeout(() => {
              finishManualDrawing();
            }, 50);
            return;
          }
        }

        const newPointIdx = drawingPoints.length;
        const newPt: Point = { x: lockedX, y: lockedY, type: 'corner' };
        let activePoints = [...drawingPoints, newPt];
        setDrawingPoints(activePoints);
        setIsPenDraggingHandle(true);

        const targetEl = e.target as HTMLElement;
        try { targetEl.setPointerCapture(e.pointerId); } catch {}

        const startClientX = e.clientX;
        const startClientY = e.clientY;

        const handlePointerMove = (moveEv: PointerEvent) => {
          const dx = (moveEv.clientX - startClientX) / zoomLevel;
          const dy = (moveEv.clientY - startClientY) / zoomLevel;

          if (Math.hypot(dx, dy) > 3) {
            const cp2 = { x: Math.round(lockedX + dx), y: Math.round(lockedY + dy) };
            const cp1 = { x: Math.round(lockedX - dx), y: Math.round(lockedY - dy) };

            activePoints = [...activePoints];
            activePoints[newPointIdx] = {
              x: lockedX,
              y: lockedY,
              cp1,
              cp2,
              type: 'smooth'
            };
            setDrawingPoints(activePoints);
          }
        };

        const handlePointerUp = (upEv: PointerEvent) => {
          try { targetEl.releasePointerCapture(upEv.pointerId); } catch {}
          setIsPenDraggingHandle(false);
          window.removeEventListener('pointermove', handlePointerMove);
          window.removeEventListener('pointerup', handlePointerUp);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        return;
      } else {
        // Pencil (Freehand) Tool Drawing Logic
        setDrawingPoints([{ x, y }]);

        const handlePointerMove = (moveEv: PointerEvent) => {
          if (!canvasRef.current) return;
          const moveRect = canvasRef.current.getBoundingClientRect();
          const mx = Math.round((moveEv.clientX - moveRect.left) / zoomLevel);
          const my = Math.round((moveEv.clientY - moveRect.top) / zoomLevel);

          setDrawingPoints(prev => {
            const last = prev[prev.length - 1];
            if (!last || Math.hypot(mx - last.x, my - last.y) >= 2) {
              return [...prev, { x: mx, y: my }];
            }
            return prev;
          });
        };

        const handlePointerUp = () => {
          window.removeEventListener('pointermove', handlePointerMove);
          window.removeEventListener('pointerup', handlePointerUp);
          // Finish drawing when user lifts pen
          setTimeout(() => {
             finishManualDrawing(drawingPointsRef.current);
          }, 0);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        return;
      }
    }

    const target = e.target as HTMLElement;
    // Skip marquee drag if user clicked directly on an element or handle
    if (target !== canvasRef.current && target.closest('.absolute')) {
      return;
    }

    const startX = x;
    const startY = y;

    const isShiftKey = e.shiftKey;
    if (!isShiftKey) {
      setSelectedElementIds([]);
    }

    setMarqueeBox({ startX, startY, currentX: startX, currentY: startY });

    const handlePointerMove = (ev: PointerEvent) => {
      if (!canvasRef.current) return;
      const currentRect = canvasRef.current.getBoundingClientRect();
      const currX = Math.round((ev.clientX - currentRect.left) / zoomLevel);
      const currY = Math.round((ev.clientY - currentRect.top) / zoomLevel);

      setMarqueeBox({ startX, startY, currentX: currX, currentY: currY });

      const boxX = Math.min(startX, currX);
      const boxY = Math.min(startY, currY);
      const boxW = Math.abs(currX - startX);
      const boxH = Math.abs(currY - startY);

      if (boxW > 2 || boxH > 2) {
        const intersecting = template.elements.filter(el => {
          const elX = el.x;
          const elY = el.y;
          const elW = el.width || 100;
          const elH = el.height || 100;

          return (
            elX < boxX + boxW &&
            elX + elW > boxX &&
            elY < boxY + boxH &&
            elY + elH > boxY
          );
        }).map(el => el.id);

        if (isShiftKey) {
          const expandedIntersecting = expandSelectionGroups(intersecting, template.elements);
          setSelectedElementIds(prev => Array.from(new Set([...prev, ...expandedIntersecting])));
        } else {
          const expandedIntersecting = expandSelectionGroups(intersecting, template.elements);
          setSelectedElementIds(expandedIntersecting);
        }
      }
    };

    const handlePointerUp = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      setMarqueeBox(null);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  const handleCanvasPointerMove = (e: React.PointerEvent) => {
    // Optimization: Only track canvas mouse position when drawing to prevent massive React re-render cascades on hover
    if (!drawingMode || isPenDraggingHandle) return;
    
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.round((e.clientX - rect.left) / zoomLevel);
    const y = Math.round((e.clientY - rect.top) / zoomLevel);
    setMouseCanvasPos({ x, y });
  };

  const handleCanvasPointerLeave = () => {
    setMouseCanvasPos(null);
  };

  // Zoom Dragging
  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      if (!zoomDrag) return;
      
      const deltaY = zoomDrag.startY - e.clientY; 
      // drag up = zoom in, drag down = zoom out.
      // 100px = 0.5 zoom step
      let newZoom = zoomDrag.startZoom + (deltaY / 200);
      newZoom = Math.max(0.1, Math.min(newZoom, 5)); // Restrict zoom between 10% and 500%
      setZoomLevel(newZoom);
      setIsFitToPage(false);
    };

    const handlePointerUp = () => {
      setZoomDrag(null);
    };

    if (zoomDrag) {
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    }

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [zoomDrag]);

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent taking over shortcuts if user is typing in an input
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'SELECT' || document.activeElement?.tagName === 'TEXTAREA') {
        return;
      }

      if (e.code === 'Space') {
        if (!e.repeat) {
          e.preventDefault();
          spacePressedRef.current = true;
          setIsHandToolActive(true);
        }
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setSelectedElementIds(template.elements.map(el => el.id));
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        handleRedo();
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c' && selectedElementIds.length > 0) {
        e.preventDefault();
        const els = template.elements.filter(e => selectedElementIds.includes(e.id));
        if (els.length > 0) setClipboard(els);
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v' && clipboard) {
        e.preventDefault();
        handlePasteElement();
        return;
      }

      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedElementIds.length > 0) {
        e.preventDefault();
        handleDeleteElement(selectedElementIds);
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd' && selectedElementIds.length > 0) {
        e.preventDefault();
        handleDuplicateElement(selectedElementIds);
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        if (e.shiftKey) {
          handleUngroupSelectedElements();
        } else {
          handleGroupSelectedElements();
        }
        return;
      }

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && selectedElementIds.length > 0) {
        e.preventDefault();
        const nudgeAmount = e.shiftKey ? 10 : 1;
        let dx = 0;
        let dy = 0;
        if (e.key === 'ArrowUp') dy = -nudgeAmount;
        if (e.key === 'ArrowDown') dy = nudgeAmount;
        if (e.key === 'ArrowLeft') dx = -nudgeAmount;
        if (e.key === 'ArrowRight') dx = nudgeAmount;

        setTemplate(prev => ({
          ...prev,
          elements: prev.elements.map(el => {
            if (selectedElementIds.includes(el.id) && !el.locked) {
              return { ...el, x: el.x + dx, y: el.y + dy };
            }
            return el;
          })
        }));
        return;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        spacePressedRef.current = false;
        setIsHandToolActive(false);
        setIsPreviewCanvasActive(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [selectedElementId, selectedElementIds, template.elements, clipboard, history, historyIndex]);

  const selectedElement = template.elements.find(e => e.id === selectedElementId);

  const handleBringForward = () => {
    if (!selectedElementId) return;
    const sorted = [...template.elements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
    const idx = sorted.findIndex(el => el.id === selectedElementId);
    if (idx === -1 || idx === sorted.length - 1) return;
    
    // Swap zIndex with the next one
    const currentZ = sorted[idx].zIndex || idx;
    const nextZ = sorted[idx + 1].zIndex || (idx + 1);
    
    setTemplate({
      ...template,
      elements: template.elements.map(el => {
        if (el.id === sorted[idx].id) return { ...el, zIndex: nextZ };
        if (el.id === sorted[idx+1].id) return { ...el, zIndex: currentZ };
        return el;
      })
    });
  };

  const handleSendBackward = () => {
    if (!selectedElementId) return;
    const sorted = [...template.elements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
    const idx = sorted.findIndex(el => el.id === selectedElementId);
    if (idx <= 0) return;
    
    // Swap zIndex with the previous one
    const currentZ = sorted[idx].zIndex || idx;
    const prevZ = sorted[idx - 1].zIndex || (idx - 1);
    
    setTemplate({
      ...template,
      elements: template.elements.map(el => {
        if (el.id === sorted[idx].id) return { ...el, zIndex: prevZ };
        if (el.id === sorted[idx-1].id) return { ...el, zIndex: currentZ };
        return el;
      })
    });
  };

  const handleLoadTemplateFromModal = (newTpl: VdpTemplate, newLayout?: VdpLayout, newUnit?: Unit) => {
    setTemplate(newTpl);
    if (newLayout) {
      setLayout(newLayout);
    }
    if (newUnit) {
      setAppUnit(newUnit);
    }
    // Auto sync/create CSV data column mappings for all template elements (QR, Barcode, Serial, Text, etc.)
    const syncedCsv = autoSyncCsvForTemplate(newTpl, csvData);
    setCsvData(syncedCsv);
  };

  return (
    <div 
      className="flex h-full w-full bg-zinc-950 overflow-hidden relative"
      onContextMenu={(e) => {
        // Only prevent default on the main background to avoid browser right click,
        // but handleContextMenu will show the custom menu.
        handleContextMenu(e, null);
      }}
    >
      {/* Toast Notification Banner */}
      {toastNotification && (
        <div className="fixed top-5 right-5 z-[999999] bg-emerald-600 text-white font-medium text-xs px-4 py-2.5 rounded-lg shadow-xl flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
          <Check className="w-4 h-4" /> {toastNotification}
        </div>
      )}

      {/* 
        Centralized Window Manager wrapper: 
        Hides the entire 2D design workspace when the 3D Proof Viewer is active
        to prevent event overlaps, layout conflicts, and bleeding elements.
      */}
      <div className="flex flex-1 w-full h-full overflow-hidden relative">

      {/* Mobile Drawer Overlay */}
      {isMobileSidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/60 z-30" 
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Tools & Properties Sidebar */}
      <div className={`${isLeftSidebarCollapsed ? 'w-16' : 'w-72 lg:w-64 xl:w-72'} bg-zinc-900 border-r border-zinc-800 flex flex-col shrink-0 shadow-lg z-40 h-full absolute lg:relative transition-[width] duration-300 ease-in-out ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} ${isLeftSidebarCollapsed ? 'overflow-visible' : 'overflow-hidden'}`}>
        
        {isLeftSidebarCollapsed && (
          <div className="flex flex-col items-center py-4 gap-4 h-full w-16 shrink-0 absolute left-0 top-0 z-10 bg-zinc-900 border-r border-zinc-800">
            <button onClick={toggleLeftSidebar} className="p-2 text-zinc-400 hover:text-white rounded-lg hover:bg-zinc-800 transition-colors" title="Expand Tools Panel">
              <PanelLeftOpen className="w-5 h-5" />
            </button>
            <div className="w-8 h-[1px] bg-zinc-800 my-1" />
            <button onClick={() => { setActiveLeftFlyout(activeLeftFlyout === 'settings' ? null : 'settings'); setIsStickerSettingsExpanded(true); setIsBrandPaletteExpanded(false); setIsAddElementsExpanded(false); setIsSmartVariablesExpanded(false); }} className={`p-2 rounded-lg hover:bg-zinc-800 transition-colors ${activeLeftFlyout === 'settings' ? 'text-blue-400' : 'text-zinc-400 hover:text-white'}`} title="Sticker Settings">
              <Settings2 className="w-5 h-5" />
            </button>
            <button onClick={() => { setActiveLeftFlyout(activeLeftFlyout === 'palette' ? null : 'palette'); setIsBrandPaletteExpanded(true); setIsStickerSettingsExpanded(false); setIsAddElementsExpanded(false); setIsSmartVariablesExpanded(false); }} className={`p-2 rounded-lg hover:bg-zinc-800 transition-colors ${activeLeftFlyout === 'palette' ? 'text-pink-400' : 'text-zinc-400 hover:text-white'}`} title="Brand Color Palette">
              <Palette className="w-5 h-5 text-pink-400" />
            </button>
            <button onClick={() => { setShowTemplateManager(true); setActiveLeftFlyout(null); }} className="p-2 text-blue-400 hover:text-blue-300 rounded-lg hover:bg-zinc-800 transition-colors" title="Templates">
              <FolderOpen className="w-5 h-5" />
            </button>
            <div className="w-8 h-[1px] bg-zinc-800 my-1" />
            <button onClick={() => { setActiveLeftFlyout(activeLeftFlyout === 'elements' ? null : 'elements'); setIsAddElementsExpanded(true); setIsStickerSettingsExpanded(false); setIsBrandPaletteExpanded(false); setIsSmartVariablesExpanded(false); }} className={`p-2 rounded-lg hover:bg-zinc-800 transition-colors ${activeLeftFlyout === 'elements' ? 'text-blue-400' : 'text-zinc-400 hover:text-white'}`} title="Add Elements">
              <Plus className="w-5 h-5" />
            </button>
            <button onClick={() => { setActiveLeftFlyout(activeLeftFlyout === 'variables' ? null : 'variables'); setIsSmartVariablesExpanded(true); setIsStickerSettingsExpanded(false); setIsBrandPaletteExpanded(false); setIsAddElementsExpanded(false); }} className={`p-2 rounded-lg hover:bg-zinc-800 transition-colors ${activeLeftFlyout === 'variables' ? 'text-blue-400' : 'text-zinc-400 hover:text-white'}`} title="Smart Variables">
              <Database className="w-5 h-5" />
            </button>
            <div className="w-8 h-[1px] bg-zinc-800 my-1" />
            <button onClick={() => handleAddElement('text')} className="p-2 text-zinc-400 hover:text-white rounded-lg hover:bg-zinc-800 transition-colors" title="Add Text">
              <Type className="w-5 h-5 text-blue-400" />
            </button>
            <button onClick={() => handleAddElement('image')} className="p-2 text-zinc-400 hover:text-white rounded-lg hover:bg-zinc-800 transition-colors" title="Add Image">
              <ImageIcon className="w-5 h-5 text-emerald-400" />
            </button>
            <button onClick={() => handleAddElement('rectangle')} className="p-2 text-zinc-400 hover:text-white rounded-lg hover:bg-zinc-800 transition-colors" title="Add Shape">
              <Square className="w-5 h-5 text-blue-500" />
            </button>
            <button onClick={() => handleAddElement('qr')} className="p-2 text-zinc-400 hover:text-white rounded-lg hover:bg-zinc-800 transition-colors" title="Add QR Code">
              <QrCode className="w-5 h-5 text-purple-400" />
            </button>
            <button onClick={() => handleAddElement('barcode')} className="p-2 text-zinc-400 hover:text-white rounded-lg hover:bg-zinc-800 transition-colors" title="Add Barcode">
              <BarcodeIcon className="w-5 h-5 text-orange-400" />
            </button>
          </div>
        )}

        <AnimatePresence>
          {(!isLeftSidebarCollapsed || activeLeftFlyout) && (
            <motion.div 
              className={`${isLeftSidebarCollapsed ? 'absolute left-16 top-0 bottom-0 w-72 lg:w-64 xl:w-72 shadow-2xl border-r border-zinc-800 z-50 bg-zinc-900 flex flex-col h-full overflow-y-auto' : 'flex-1 flex flex-col w-full h-full'}`}
              variants={macPanelVariants('left')}
              initial={isLeftSidebarCollapsed ? "initial" : false}
              animate={isLeftSidebarCollapsed ? "animate" : false}
              exit={isLeftSidebarCollapsed ? "exit" : false}
              transition={macSpring}
            >
        {/* 1. Sticker Settings Section */}
        {(!activeLeftFlyout || activeLeftFlyout === 'settings') && (
        <div className="border-b border-zinc-800 bg-zinc-950" id="tour-template-dimension-bar">
          <div 
            onClick={() => setIsStickerSettingsExpanded(!isStickerSettingsExpanded)}
            className="p-3.5 flex items-center justify-between cursor-pointer select-none hover:bg-zinc-900/80 transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="p-0.5 text-zinc-400 hover:text-white transition-colors">
                {isStickerSettingsExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </span>
              <h3 className="font-semibold text-zinc-200 text-sm">Sticker Settings</h3>
            </div>
            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
              <button onClick={toggleLeftSidebar} className="text-zinc-500 hover:text-zinc-300 p-1 rounded hover:bg-zinc-800 transition-colors" title="Collapse Panel">
                <PanelLeftClose className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          {isStickerSettingsExpanded && (
            <div className="px-4 pb-4 pt-1 border-t border-zinc-800/60">
              <div className="flex items-center gap-2 mb-4 justify-between">
                <button 
                  onClick={handleNewDocument}
                  className="flex-1 text-[11px] bg-zinc-800 border border-zinc-700 text-zinc-300 py-1.5 rounded shadow-sm hover:bg-zinc-700 transition-colors flex items-center justify-center gap-1.5 cursor-pointer font-medium"
                >
                  <Plus className="w-3.5 h-3.5" /> New
                </button>
                <button 
                  onClick={handleQuickSave}
                  className="flex-1 text-[11px] bg-zinc-800 border border-zinc-700 text-zinc-300 py-1.5 rounded shadow-sm hover:bg-zinc-700 transition-colors flex items-center justify-center gap-1.5 cursor-pointer font-medium"
                >
                  <Save className="w-3.5 h-3.5 text-emerald-400" /> Save
                </button>
                <button 
                  onClick={() => setShowTemplateManager(true)}
                  className="flex-1 text-[11px] bg-blue-500/10 border border-blue-500/20 text-blue-400 py-1.5 rounded shadow-sm hover:bg-blue-500/20 transition-colors flex items-center justify-center gap-1.5 cursor-pointer font-medium"
                >
                  <FolderOpen className="w-3.5 h-3.5" /> Presets
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">Width ({appUnit})</label>
                  <input 
                    type="number" 
                    className="w-full px-3 py-1.5 border border-zinc-700 rounded focus:ring-2 focus:ring-blue-600 outline-none text-sm bg-zinc-800 text-zinc-100"
                    value={formatUnit(template.width, appUnit)}
                    onChange={e => handleUpdateTemplateSize(parseUnit(e.target.value, appUnit), template.height || 200)}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">Height ({appUnit})</label>
                  <input 
                    type="number" 
                    className="w-full px-3 py-1.5 border border-zinc-700 rounded focus:ring-2 focus:ring-blue-600 outline-none text-sm bg-zinc-800 text-zinc-100"
                    value={formatUnit(template.height, appUnit)}
                    onChange={e => handleUpdateTemplateSize(template.width || 300, parseUnit(e.target.value, appUnit))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">Shape</label>
                  <select 
                    className="w-full px-3 py-1.5 border border-zinc-700 rounded focus:ring-2 focus:ring-blue-600 outline-none text-sm bg-zinc-800 text-zinc-100"
                    value={template.shape || 'rectangle'}
                    onChange={e => setTemplate({ ...template, shape: e.target.value as any })}
                  >
                    <option value="rectangle">Rectangle / Square</option>
                    <option value="circle">Circle</option>
                    <option value="oval">Oval</option>
                    <option value="custom">Custom Vector</option>
                  </select>
                </div>
                {template.shape !== 'circle' && template.shape !== 'oval' && (
                  <div>
                    <label className="text-xs font-medium text-zinc-500 mb-1 block">Corner Radius</label>
                    <input 
                      type="number" 
                      min="0"
                      className="w-full px-3 py-1.5 border border-zinc-700 rounded focus:ring-2 focus:ring-blue-600 outline-none text-sm bg-zinc-800 text-zinc-100"
                      value={template.cornerRadius || 0}
                      onChange={e => setTemplate({ ...template, cornerRadius: Number(e.target.value) })}
                    />
                  </div>
                )}
              </div>
              <div className="mb-3">
                 <label className="text-xs font-medium text-zinc-500 mb-1 block">Background Color</label>
                 <div className="flex gap-2">
                   <input 
                     type="color" 
                     className="w-8 h-8 rounded border border-zinc-700 bg-zinc-800 p-0 cursor-pointer"
                     value={template.backgroundColor === 'transparent' || template.backgroundColor === 'none' ? '#ffffff' : (template.backgroundColor || '#ffffff')}
                     onChange={e => setTemplate({ ...template, backgroundColor: e.target.value })}
                     disabled={template.backgroundColor === 'transparent'}
                   />
                   <label className="flex items-center gap-2 text-sm text-zinc-300">
                     <input 
                       type="checkbox" 
                       checked={template.backgroundColor === 'transparent'}
                       onChange={e => setTemplate({ ...template, backgroundColor: e.target.checked ? 'transparent' : '#ffffff' })}
                       className="rounded bg-zinc-800 border-zinc-700"
                     />
                     Transparent
                   </label>
                 </div>
              </div>
              
              {/* Grid Dot Spacing (Fine to Coarse) */}
              <div className="mb-3 pt-2.5 border-t border-zinc-800">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-medium text-zinc-400 flex items-center gap-1.5">
                    <Grid3X3 className="w-3.5 h-3.5 text-blue-400" />
                    <span>Grid Dot Spacing</span>
                  </label>
                  <span className="text-[11px] font-mono text-zinc-400 bg-zinc-800 px-1.5 py-0.5 rounded border border-zinc-700">
                    {gridDotSpacing}px
                  </span>
                </div>
                <input
                  type="range"
                  min="8"
                  max="48"
                  step="2"
                  value={gridDotSpacing}
                  onChange={(e) => handleUpdateGridDotSpacing(Number(e.target.value))}
                  className="w-full accent-blue-500 cursor-pointer h-1.5 bg-zinc-800 rounded-lg appearance-none"
                  title="Adjust workspace background grid dot spacing"
                />
                <div className="flex items-center justify-between text-[9px] text-zinc-500 font-medium mt-1">
                  <button
                    type="button"
                    onClick={() => handleUpdateGridDotSpacing(8)}
                    className={`hover:text-blue-400 transition-colors cursor-pointer ${gridDotSpacing === 8 ? 'text-blue-400 font-bold' : ''}`}
                  >
                    Fine (8px)
                  </button>
                  <button
                    type="button"
                    onClick={() => handleUpdateGridDotSpacing(16)}
                    className={`hover:text-blue-400 transition-colors cursor-pointer ${gridDotSpacing === 16 ? 'text-blue-400 font-bold' : ''}`}
                  >
                    Standard (16px)
                  </button>
                  <button
                    type="button"
                    onClick={() => handleUpdateGridDotSpacing(32)}
                    className={`hover:text-blue-400 transition-colors cursor-pointer ${gridDotSpacing === 32 ? 'text-blue-400 font-bold' : ''}`}
                  >
                    Coarse (32px)
                  </button>
                </div>
              </div>

              <div className="flex gap-1.5 overflow-x-auto custom-scrollbar pb-1">
                <button onClick={() => handleUpdateTemplateSize(parseUnit(2, 'in'), parseUnit(2, 'in'))} className="shrink-0 px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold bg-zinc-800 hover:bg-blue-900 text-zinc-400 hover:text-blue-300 rounded border border-zinc-700 hover:border-blue-700 transition-colors">2x2"</button>
                <button onClick={() => handleUpdateTemplateSize(parseUnit(3, 'in'), parseUnit(3, 'in'))} className="shrink-0 px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold bg-zinc-800 hover:bg-blue-900 text-zinc-400 hover:text-blue-300 rounded border border-zinc-700 hover:border-blue-700 transition-colors">3x3"</button>
                <button onClick={() => handleUpdateTemplateSize(parseUnit(4, 'in'), parseUnit(6, 'in'))} className="shrink-0 px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold bg-zinc-800 hover:bg-emerald-900 text-zinc-400 hover:text-emerald-300 rounded border border-zinc-700 hover:border-emerald-700 transition-colors">4x6" Label</button>
                <button onClick={() => handleUpdateTemplateSize(parseUnit(50, 'mm'), parseUnit(50, 'mm'))} className="shrink-0 px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold bg-zinc-800 hover:bg-purple-900 text-zinc-400 hover:text-purple-300 rounded border border-zinc-700 hover:border-purple-700 transition-colors">50mm²</button>
                <button onClick={() => handleUpdateTemplateSize(parseUnit(85, 'mm'), parseUnit(55, 'mm'))} className="shrink-0 px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold bg-zinc-800 hover:bg-purple-900 text-zinc-400 hover:text-purple-300 rounded border border-zinc-700 hover:border-purple-700 transition-colors">85x55mm Card</button>
              </div>
            </div>
          )}
        </div>
        )}

        {/* 2. Brand Color Palette Management Section */}
        {(!activeLeftFlyout || activeLeftFlyout === 'palette') && (
          <BrandPaletteSection
            template={template}
            onUpdateTemplate={setTemplate}
            selectedElements={selectedElements}
            onUpdateElements={handleUpdateSelectedElements}
            isExpanded={isBrandPaletteExpanded}
            onToggleExpand={() => setIsBrandPaletteExpanded(!isBrandPaletteExpanded)}
          />
        )}

        {/* 3. Add Elements Section */}
        {(!activeLeftFlyout || activeLeftFlyout === 'elements') && (
        <div className="border-b border-zinc-800 bg-zinc-900/60" id="tour-element-tools">
          <div 
            onClick={() => setIsAddElementsExpanded(!isAddElementsExpanded)}
            className="p-3.5 flex items-center justify-between cursor-pointer select-none hover:bg-zinc-800/80 transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="p-0.5 text-zinc-400 hover:text-white transition-colors">
                {isAddElementsExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </span>
              <h3 className="font-semibold text-zinc-200 text-sm">Add Elements</h3>
            </div>
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold px-2 py-0.5 rounded bg-zinc-800/80 border border-zinc-700/60">
              {isAddElementsExpanded ? 'Collapse' : 'Expand'}
            </span>
          </div>
          {isAddElementsExpanded && (
            <div className="px-4 pb-4 pt-1 border-t border-zinc-800/60 flex flex-col gap-4">
              
              {/* Text & Media */}
              <div>
                <h4 className="text-[10px] uppercase font-bold text-zinc-500 mb-2 tracking-wider">Basic & Media</h4>
                <div className="grid grid-cols-3 gap-2">
                  <button 
                    onClick={() => setActiveCreationTool(activeCreationTool === 'textbox' ? null : 'textbox')} 
                    className={`flex flex-col items-center gap-1 py-2 px-1 rounded font-medium shadow-sm text-xs transition-colors cursor-pointer ${
                      activeCreationTool === 'textbox' ? 'border border-blue-500 bg-blue-950/60 text-blue-300 ring-1 ring-blue-500' : 'bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 text-zinc-300'
                    }`}
                    title="Drag on Canvas to Draw Multi-line Text Box"
                  >
                    <Type className="w-4 h-4 text-blue-400" /> 
                    <span className="text-[11px]">Text Box</span>
                  </button>
                  <button 
                    onClick={() => handleAddElement('text')} 
                    className="flex flex-col items-center gap-1 py-2 px-1 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded font-medium text-zinc-300 shadow-sm text-xs transition-colors cursor-pointer"
                    title="Add Point Text"
                  >
                    <span className="font-serif font-bold text-sm text-blue-400 leading-none">T</span>
                    <span className="text-[11px]">Text</span>
                  </button>
                  <button 
                    onClick={() => handleAddElement('image')} 
                    className="flex flex-col items-center gap-1 py-2 px-1 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded font-medium text-zinc-300 shadow-sm text-xs transition-colors cursor-pointer"
                    title="Add Image"
                  >
                    <ImageIcon className="w-4 h-4 text-emerald-400" /> 
                    <span className="text-[11px]">Image</span>
                  </button>
                </div>
              </div>

              {/* Vector Shapes & Drawing */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[10px] uppercase font-bold text-zinc-500 tracking-wider">Shapes & Vectors</h4>
                  <span className="text-[10px] text-zinc-500 font-mono">11 Vector Tools</span>
                </div>
                <div className="grid grid-cols-4 gap-1.5 mb-2">
                  <button onClick={() => handleAddElement('rectangle')} className="flex flex-col items-center justify-center p-2 bg-zinc-900 border border-zinc-700/80 hover:border-blue-500 hover:bg-zinc-800 rounded-lg text-zinc-300 transition-colors group cursor-pointer" title="Add Rectangle"><Square className="w-4 h-4 text-blue-400 group-hover:scale-110 transition-transform" /><span className="text-[9px] mt-1 text-zinc-400">Rect</span></button>
                  <button onClick={() => handleAddElement('circle')} className="flex flex-col items-center justify-center p-2 bg-zinc-900 border border-zinc-700/80 hover:border-blue-500 hover:bg-zinc-800 rounded-lg text-zinc-300 transition-colors group cursor-pointer" title="Add Circle"><Circle className="w-4 h-4 text-emerald-400 group-hover:scale-110 transition-transform" /><span className="text-[9px] mt-1 text-zinc-400">Circle</span></button>
                  <button onClick={() => handleAddElement('triangle')} className="flex flex-col items-center justify-center p-2 bg-zinc-900 border border-zinc-700/80 hover:border-blue-500 hover:bg-zinc-800 rounded-lg text-zinc-300 transition-colors group cursor-pointer" title="Add Triangle"><Triangle className="w-4 h-4 text-cyan-400 group-hover:scale-110 transition-transform" /><span className="text-[9px] mt-1 text-zinc-400">Triangle</span></button>
                  <button onClick={() => handleAddElement('star')} className="flex flex-col items-center justify-center p-2 bg-zinc-900 border border-zinc-700/80 hover:border-blue-500 hover:bg-zinc-800 rounded-lg text-zinc-300 transition-colors group cursor-pointer" title="Add Star"><Star className="w-4 h-4 text-amber-400 group-hover:scale-110 transition-transform" /><span className="text-[9px] mt-1 text-zinc-400">Star</span></button>
                </div>
                <div className="grid grid-cols-4 gap-1.5 mb-2">
                  <button onClick={() => handleAddElement('polygon')} className="flex flex-col items-center justify-center p-2 bg-zinc-900 border border-zinc-700/80 hover:border-blue-500 hover:bg-zinc-800 rounded-lg text-zinc-300 transition-colors group cursor-pointer" title="Add Polygon"><Hexagon className="w-4 h-4 text-indigo-400 group-hover:scale-110 transition-transform" /><span className="text-[9px] mt-1 text-zinc-400">Polygon</span></button>
                  <button onClick={() => handleAddElement('diamond')} className="flex flex-col items-center justify-center p-2 bg-zinc-900 border border-zinc-700/80 hover:border-blue-500 hover:bg-zinc-800 rounded-lg text-zinc-300 transition-colors group cursor-pointer" title="Add Diamond"><Diamond className="w-4 h-4 text-purple-400 group-hover:scale-110 transition-transform" /><span className="text-[9px] mt-1 text-zinc-400">Diamond</span></button>
                  <button onClick={() => handleAddElement('heart')} className="flex flex-col items-center justify-center p-2 bg-zinc-900 border border-zinc-700/80 hover:border-blue-500 hover:bg-zinc-800 rounded-lg text-zinc-300 transition-colors group cursor-pointer" title="Add Heart"><Heart className="w-4 h-4 text-rose-400 group-hover:scale-110 transition-transform" /><span className="text-[9px] mt-1 text-zinc-400">Heart</span></button>
                  <button onClick={() => handleAddElement('arrow')} className="flex flex-col items-center justify-center p-2 bg-zinc-900 border border-zinc-700/80 hover:border-blue-500 hover:bg-zinc-800 rounded-lg text-zinc-300 transition-colors group cursor-pointer" title="Add Arrow"><ArrowRight className="w-4 h-4 text-teal-400 group-hover:scale-110 transition-transform" /><span className="text-[9px] mt-1 text-zinc-400">Arrow</span></button>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  <button onClick={() => handleAddElement('line')} className="flex items-center justify-center gap-1.5 py-2 px-2 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded-lg text-xs font-medium text-zinc-300 transition-colors cursor-pointer" title="Add Vector Line"><Minus className="w-4 h-4 text-zinc-300" /> Line</button>
                  <button onClick={() => startManualDrawing('unified', 'pen')} className="flex items-center justify-center gap-1.5 py-2 px-2 bg-indigo-950/60 hover:bg-indigo-900/80 border border-indigo-700/60 rounded-lg text-xs font-medium text-indigo-300 transition-colors cursor-pointer" title="Vector Pen Tool (Drop points & Bezier handles)"><PenTool className="w-3.5 h-3.5 text-indigo-400" /> Pen</button>
                  <button onClick={() => startManualDrawing('unified', 'pencil')} className="flex items-center justify-center gap-1.5 py-2 px-2 bg-amber-950/60 hover:bg-amber-900/80 border border-amber-700/60 rounded-lg text-xs font-medium text-amber-300 transition-colors cursor-pointer" title="Pencil Freehand Vector Brush"><Pencil className="w-3.5 h-3.5 text-amber-400" /> Pencil</button>
                </div>
              </div>

              {/* Dedicated Pathfinder & Boolean Section in Sidebar */}
              <div className="p-3 bg-zinc-950/70 border border-indigo-900/40 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <Combine className="w-3.5 h-3.5 text-indigo-400" />
                    <h4 className="text-[10px] uppercase font-bold text-indigo-300 tracking-wider">Pathfinder Tools</h4>
                  </div>
                  <button
                    onClick={() => setShowPathfinderPanel(true)}
                    className="text-[10px] text-indigo-400 hover:text-indigo-300 font-medium underline cursor-pointer"
                  >
                    Open Panel
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-1.5 mb-2">
                  <button
                    onClick={() => handleApplyPathfinder('unite')}
                    disabled={selectedElementIds.length < 2}
                    className="flex flex-col items-center justify-center p-1.5 rounded-lg bg-zinc-900 border border-zinc-800 hover:border-indigo-500 hover:bg-indigo-950/60 disabled:opacity-40 disabled:hover:border-zinc-800 disabled:hover:bg-zinc-900 transition-all cursor-pointer disabled:cursor-not-allowed"
                    title="Unite: Combine selected shapes into single geometry"
                  >
                    <PathfinderUnionIcon className="w-3.5 h-3.5 text-indigo-400" />
                    <span className="text-[8px] mt-0.5 text-zinc-300">Unite</span>
                  </button>
                  <button
                    onClick={() => handleApplyPathfinder('subtract')}
                    disabled={selectedElementIds.length < 2}
                    className="flex flex-col items-center justify-center p-1.5 rounded-lg bg-zinc-900 border border-zinc-800 hover:border-indigo-500 hover:bg-indigo-950/60 disabled:opacity-40 disabled:hover:border-zinc-800 disabled:hover:bg-zinc-900 transition-all cursor-pointer disabled:cursor-not-allowed"
                    title="Subtract: Subtract top shapes from bottom shape"
                  >
                    <PathfinderSubtractIcon className="w-3.5 h-3.5 text-rose-400" />
                    <span className="text-[8px] mt-0.5 text-zinc-300">Subtract</span>
                  </button>
                  <button
                    onClick={() => handleApplyPathfinder('intersect')}
                    disabled={selectedElementIds.length < 2}
                    className="flex flex-col items-center justify-center p-1.5 rounded-lg bg-zinc-900 border border-zinc-800 hover:border-indigo-500 hover:bg-indigo-950/60 disabled:opacity-40 disabled:hover:border-zinc-800 disabled:hover:bg-zinc-900 transition-all cursor-pointer disabled:cursor-not-allowed"
                    title="Intersect: Keep overlapping areas"
                  >
                    <PathfinderIntersectIcon className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-[8px] mt-0.5 text-zinc-300">Intersect</span>
                  </button>
                  <button
                    onClick={() => handleApplyPathfinder('exclude')}
                    disabled={selectedElementIds.length < 2}
                    className="flex flex-col items-center justify-center p-1.5 rounded-lg bg-zinc-900 border border-zinc-800 hover:border-indigo-500 hover:bg-indigo-950/60 disabled:opacity-40 disabled:hover:border-zinc-800 disabled:hover:bg-zinc-900 transition-all cursor-pointer disabled:cursor-not-allowed"
                    title="Exclude: XOR cutout overlapping areas"
                  >
                    <PathfinderExcludeIcon className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="text-[8px] mt-0.5 text-zinc-300">Exclude</span>
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    onClick={() => handleApplyPathfinder('divide')}
                    disabled={selectedElementIds.length < 2}
                    className="flex items-center justify-center gap-1 py-1 px-1.5 rounded-lg bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-[10px] text-purple-300 disabled:opacity-40 transition-colors cursor-pointer disabled:cursor-not-allowed"
                    title="Divide intersecting paths"
                  >
                    <Scissors className="w-3 h-3 text-purple-400" /> Divide
                  </button>
                  <button
                    onClick={() => {
                      if (selectedElementIds.length === 0) return;
                      const selectedIds = new Set(selectedElementIds);
                      setTemplate(prev => ({
                        ...prev,
                        elements: prev.elements.map(el => selectedIds.has(el.id) ? {
                          ...el,
                          isCutline: true,
                          borderColor: '#ec4899',
                          strokeColor: '#ec4899',
                          strokeWidth: 2,
                          lineDash: 'dashed',
                          backgroundColor: 'transparent',
                          isTransparentBg: true
                        } : el)
                      }));
                      setToastNotification('Converted to Pink Plotter Cutline Contour');
                      setTimeout(() => setToastNotification(null), 2500);
                    }}
                    disabled={selectedElementIds.length === 0}
                    className="flex items-center justify-center gap-1 py-1 px-1.5 rounded-lg bg-pink-950/50 border border-pink-700/50 hover:bg-pink-900/60 text-[10px] text-pink-300 disabled:opacity-40 transition-colors cursor-pointer disabled:cursor-not-allowed"
                    title="Convert selected elements to Pink Die-Cut Line"
                  >
                    <Flame className="w-3 h-3 text-pink-400" /> Die-Cut
                  </button>
                </div>
              </div>

              {/* Barcodes & Tracking */}
              <div>
                <h4 className="text-[10px] uppercase font-bold text-zinc-500 mb-2 tracking-wider">Barcodes & Codes</h4>
                <div className="grid grid-cols-3 gap-2">
                  <button onClick={() => handleAddElement('qr')} className="flex flex-col items-center gap-1 py-2 px-1 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded text-xs font-medium text-zinc-300 transition-colors">
                    <QrCode className="w-4 h-4 text-purple-400" /> QR Code
                  </button>
                  <button onClick={() => handleAddElement('barcode')} className="flex flex-col items-center gap-1 py-2 px-1 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded text-xs font-medium text-zinc-300 transition-colors">
                    <BarcodeIcon className="w-4 h-4 text-purple-400" /> Barcode
                  </button>
                  <button onClick={() => handleAddElement('datamatrix')} className="flex flex-col items-center gap-1 py-2 px-1 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded text-[10px] font-medium text-zinc-300 transition-colors">
                    <Grid3X3 className="w-4 h-4 text-purple-400" /> DMatrix
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button onClick={() => handleAddElement('ean13')} className="flex items-center justify-center gap-1.5 py-1.5 px-2 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded text-xs text-zinc-300 transition-colors">
                    EAN-13
                  </button>
                  <button onClick={() => handleAddElement('upca')} className="flex items-center justify-center gap-1.5 py-1.5 px-2 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded text-xs text-zinc-300 transition-colors">
                    UPC-A
                  </button>
                </div>
              </div>

              {/* Variable Data */}
              <div>
                <h4 className="text-[10px] uppercase font-bold text-zinc-500 mb-2 tracking-wider">Smart Variables</h4>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => handleAddElement('serial')} className="flex items-center gap-2 py-2 px-2 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded text-xs font-medium text-zinc-300 transition-colors">
                    <Hash className="w-3.5 h-3.5 text-rose-400" /> Serial No.
                  </button>
                  <button onClick={() => handleAddElement('date')} className="flex items-center gap-2 py-2 px-2 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded text-xs font-medium text-zinc-300 transition-colors">
                    <Calendar className="w-3.5 h-3.5 text-rose-400" /> Date
                  </button>
                  <button onClick={() => handleAddElement('currency')} className="flex items-center gap-2 py-2 px-2 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded text-xs font-medium text-zinc-300 transition-colors">
                    <DollarSign className="w-3.5 h-3.5 text-green-400" /> Price
                  </button>
                  <button onClick={() => handleAddElement('uppercase')} className="flex items-center gap-2 py-2 px-2 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded text-xs font-medium text-zinc-300 transition-colors">
                    <ALargeSmall className="w-3.5 h-3.5 text-rose-400" /> UPPERCASE
                  </button>
                </div>
              </div>

            </div>
          )}
        </div>
        )}

        {/* 4. Smart Variables & Dynamic Data Section */}
        {(!activeLeftFlyout || activeLeftFlyout === 'variables') && (
          <div className="border-b border-zinc-800 bg-zinc-900/60" id="tour-smart-variables">
            <div 
              onClick={() => setIsSmartVariablesExpanded(!isSmartVariablesExpanded)}
              className="p-3.5 flex items-center justify-between cursor-pointer select-none hover:bg-zinc-800/80 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="p-0.5 text-zinc-400 hover:text-white transition-colors">
                  {isSmartVariablesExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </span>
                <div className="flex items-center gap-1.5">
                  <Database className="w-4 h-4 text-blue-400" />
                  <h3 className="font-semibold text-zinc-200 text-sm">Smart Variables</h3>
                </div>
              </div>
              <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                <span className="text-[10px] uppercase tracking-wider text-blue-400 font-semibold px-2 py-0.5 rounded bg-blue-500/10 border border-blue-500/20">
                  {csvData?.headers?.length || 0} Fields
                </span>
                {activeLeftFlyout === 'variables' && (
                  <button 
                    onClick={() => setActiveLeftFlyout(null)}
                    className="text-zinc-500 hover:text-zinc-300 p-1 rounded hover:bg-zinc-800 transition-colors cursor-pointer"
                    title="Close Panel"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {isSmartVariablesExpanded && (
              <div className="px-4 pb-4 pt-1 border-t border-zinc-800/60 flex flex-col gap-4">
                
                {/* Dataset Overview & Preview Switcher */}
                <div className="p-2.5 bg-zinc-950/80 border border-zinc-800 rounded-lg">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                      Data Source
                    </span>
                    <span className="text-[11px] font-mono text-zinc-300 font-medium">
                      {csvData?.rows?.length || 0} Records
                    </span>
                  </div>

                  {/* Record Preview Stepper */}
                  {csvData && csvData.rows && csvData.rows.length > 0 && (
                    <div className="flex items-center justify-between gap-1 pt-1.5 border-t border-zinc-800/60 text-xs">
                      <span className="text-zinc-400 text-[11px]">Previewing:</span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setPreviewRecordIndex(prev => Math.max(0, prev - 1))}
                          disabled={previewRecordIndex <= 0}
                          className="px-1.5 py-0.5 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 rounded text-zinc-300 transition-colors cursor-pointer text-[10px]"
                          title="Previous record"
                        >
                          ◀
                        </button>
                        <span className="font-mono text-zinc-200 text-[11px] px-1.5">
                          #{previewRecordIndex + 1} / {csvData.rows.length}
                        </span>
                        <button
                          onClick={() => setPreviewRecordIndex(prev => Math.min(csvData.rows.length - 1, prev + 1))}
                          disabled={previewRecordIndex >= csvData.rows.length - 1}
                          className="px-1.5 py-0.5 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 rounded text-zinc-300 transition-colors cursor-pointer text-[10px]"
                          title="Next record"
                        >
                          ▶
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Quick Add Built-in Smart Variable Elements */}
                <div>
                  <h4 className="text-[10px] uppercase font-bold text-zinc-500 mb-2 tracking-wider">
                    Quick Dynamic Elements
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <button 
                      onClick={() => handleAddElement('serial')} 
                      className="flex items-center gap-2 py-2 px-2.5 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded font-medium text-zinc-300 shadow-sm text-xs transition-colors group cursor-pointer"
                      title="Add auto-incrementing serial number"
                    >
                      <Hash className="w-3.5 h-3.5 text-rose-400 group-hover:scale-110 transition-transform" /> 
                      <span>Serial No.</span>
                    </button>
                    <button 
                      onClick={() => handleAddElement('date')} 
                      className="flex items-center gap-2 py-2 px-2.5 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded font-medium text-zinc-300 shadow-sm text-xs transition-colors group cursor-pointer"
                      title="Add dynamic timestamp / expiration date"
                    >
                      <Calendar className="w-3.5 h-3.5 text-rose-400 group-hover:scale-110 transition-transform" /> 
                      <span>Date Stamp</span>
                    </button>
                    <button 
                      onClick={() => handleAddElement('currency')} 
                      className="flex items-center gap-2 py-2 px-2.5 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded font-medium text-zinc-300 shadow-sm text-xs transition-colors group cursor-pointer"
                      title="Add dynamic currency price tag"
                    >
                      <DollarSign className="w-3.5 h-3.5 text-green-400 group-hover:scale-110 transition-transform" /> 
                      <span>Price Tag</span>
                    </button>
                    <button 
                      onClick={() => handleAddElement('uppercase')} 
                      className="flex items-center gap-2 py-2 px-2.5 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded font-medium text-zinc-300 shadow-sm text-xs transition-colors group cursor-pointer"
                      title="Add uppercase formatted text"
                    >
                      <ALargeSmall className="w-3.5 h-3.5 text-blue-400 group-hover:scale-110 transition-transform" /> 
                      <span>UPPERCASE</span>
                    </button>
                  </div>
                </div>

                {/* Available CSV Columns (Click to insert bound element) */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-[10px] uppercase font-bold text-zinc-500 tracking-wider">
                      Dataset Columns (Click to Add)
                    </h4>
                    <button
                      onClick={() => setShowAddVarInput(!showAddVarInput)}
                      className="text-[10px] text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1 cursor-pointer transition-colors"
                    >
                      <Plus className="w-3 h-3" /> Custom
                    </button>
                  </div>

                  {showAddVarInput && (
                    <div className="mb-2 p-2 bg-zinc-950 border border-blue-500/40 rounded-lg flex flex-col gap-1.5 animate-in fade-in duration-150">
                      <span className="text-[10px] text-zinc-400 font-medium">New Column Name:</span>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          value={newCustomVarName}
                          onChange={(e) => setNewCustomVarName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleCreateCustomVariable(); }}
                          placeholder="e.g. Batch_No or SKU"
                          className="flex-1 px-2 py-1 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-100 outline-none focus:border-blue-500 font-mono"
                          autoFocus
                        />
                        <button
                          onClick={handleCreateCustomVariable}
                          className="px-2.5 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-semibold cursor-pointer transition-colors"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  )}

                  {csvData && csvData.headers && csvData.headers.length > 0 ? (
                    <div className="flex flex-col gap-1.5 max-h-56 overflow-y-auto pr-0.5 custom-scrollbar">
                      {csvData.headers.map((col) => {
                        const isMapped = template.elements.some(e => e.columnName === col);
                        const sampleVal = csvData.rows?.[previewRecordIndex]?.[col] || csvData.rows?.[0]?.[col] || '';

                        return (
                          <div
                            key={col}
                            onClick={() => handleAddColumnElement(col, 'text')}
                            className={`p-2 rounded-lg border transition-all cursor-pointer flex items-center justify-between group ${
                              isMapped
                                ? 'bg-zinc-900/90 border-blue-500/30 hover:border-blue-400 hover:bg-zinc-800'
                                : 'bg-zinc-950/70 border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900'
                            }`}
                            title={`Click to add {{${col}}} text element to design canvas`}
                          >
                            <div className="flex flex-col min-w-0 pr-2">
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono text-xs font-semibold text-blue-300 truncate">
                                  {`{{${col}}}`}
                                </span>
                                {isMapped && (
                                  <span className="text-[9px] px-1 py-0.2 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-medium">
                                    Mapped
                                  </span>
                                )}
                              </div>
                              {sampleVal && (
                                <span className="text-[10px] text-zinc-500 truncate mt-0.5">
                                  {String(sampleVal)}
                                </span>
                              )}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddColumnElement(col, 'text');
                              }}
                              className="p-1 rounded bg-zinc-800/80 group-hover:bg-blue-600 group-hover:text-white text-zinc-400 transition-colors shrink-0"
                              title="Insert as text"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-3 bg-zinc-950 border border-dashed border-zinc-800 rounded-lg text-center text-xs text-zinc-500">
                      No CSV columns found. Add a custom variable above or upload a CSV in Step 2.
                    </div>
                  )}
                </div>

                {/* Dynamic Codes linked to variables */}
                <div>
                  <h4 className="text-[10px] uppercase font-bold text-zinc-500 mb-2 tracking-wider">
                    Dynamic Codes
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleAddElement('qr')}
                      className="flex items-center gap-2 py-2 px-2 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded text-xs font-medium text-zinc-300 transition-colors cursor-pointer"
                      title="Add QR Code linked to dynamic variable"
                    >
                      <QrCode className="w-3.5 h-3.5 text-purple-400" /> Dynamic QR
                    </button>
                    <button
                      onClick={() => handleAddElement('barcode')}
                      className="flex items-center gap-2 py-2 px-2 bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 rounded text-xs font-medium text-zinc-300 transition-colors cursor-pointer"
                      title="Add Barcode linked to dynamic variable"
                    >
                      <BarcodeIcon className="w-3.5 h-3.5 text-orange-400" /> Dynamic Barcode
                    </button>
                  </div>
                </div>

              </div>
            )}
          </div>
        )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {/* Central Interactive Canvas View */}
      <div className="flex-1 bg-zinc-950 flex flex-col relative overflow-hidden min-h-0 h-full isolate z-10">
        {/* Canvas Control Toolbar - Single Sleek Row with Icon Buttons & Tooltips */}
        <div className="px-3 py-1.5 border-b border-zinc-800 bg-zinc-900/95 backdrop-blur-md flex flex-nowrap items-center justify-between gap-2 z-20 overflow-x-auto custom-scrollbar shrink-0 min-h-[42px]">
          {/* Left: Quick Viewport & Canvas Controls */}
          <div className="flex items-center gap-1.5 shrink-0">
            {/* Mobile Sidebar Toggle */}
            <button
              onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
              className="lg:hidden p-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors cursor-pointer"
              title="Toggle Tools Panel"
            >
              <Settings2 className="w-4 h-4" />
            </button>
            <div className="lg:hidden h-4 w-[1px] bg-zinc-800 mx-1"></div>

            <button
              onClick={() => handleUndo()}
              disabled={historyIndex <= 0}
              className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-md transition-colors disabled:opacity-30 cursor-pointer"
              title="Undo (Ctrl+Z)"
            >
              <Undo className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleRedo()}
              disabled={historyIndex >= history.length - 1}
              className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-md transition-colors disabled:opacity-30 cursor-pointer"
              title="Redo (Ctrl+Y)"
            >
              <Redo className="w-4 h-4" />
            </button>
            
            <div className="h-4 w-px bg-zinc-800 mx-0.5"></div>

            {/* Direct Canvas Creation Tools (Select, Text Box, Text, Shapes, Pen) */}
            <div className="flex items-center bg-zinc-950 rounded-lg border border-zinc-800 p-0.5 gap-0.5 shrink-0">
              <button
                onClick={() => { setActiveCreationTool(null); if (drawingMode) setDrawingMode(null); }}
                className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                  !activeCreationTool && !drawingMode
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                title="Select & Move Pointer (V)"
              >
                <MousePointer className="w-3.5 h-3.5" />
              </button>
              
              <button
                onClick={() => {
                  setActiveCreationTool(activeCreationTool === 'textbox' ? null : 'textbox');
                  if (drawingMode) setDrawingMode(null);
                }}
                className={`px-2 py-1 rounded-md transition-colors cursor-pointer text-xs flex items-center gap-1.5 font-medium ${
                  activeCreationTool === 'textbox'
                    ? 'bg-blue-600 text-white shadow-xs font-semibold'
                    : 'text-zinc-300 hover:text-white hover:bg-zinc-800'
                }`}
                title="Drag on Canvas to Draw Text Box (T)"
              >
                <Type className="w-3.5 h-3.5 text-blue-400" />
                <span className="hidden sm:inline text-[11px]">Text Box</span>
              </button>

              <button
                onClick={() => {
                  setActiveCreationTool(activeCreationTool === 'text' ? null : 'text');
                  if (drawingMode) setDrawingMode(null);
                }}
                className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                  activeCreationTool === 'text'
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                title="Drag to Draw Single-line Text"
              >
                <span className="font-serif font-bold text-xs leading-none">T</span>
              </button>

              <button
                onClick={() => {
                  setActiveCreationTool(activeCreationTool === 'rectangle' ? null : 'rectangle');
                  if (drawingMode) setDrawingMode(null);
                }}
                className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                  activeCreationTool === 'rectangle'
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                title="Drag to Draw Rectangle on Canvas (R)"
              >
                <Square className="w-3.5 h-3.5" />
              </button>

              <button
                onClick={() => {
                  setActiveCreationTool(activeCreationTool === 'circle' ? null : 'circle');
                  if (drawingMode) setDrawingMode(null);
                }}
                className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                  activeCreationTool === 'circle'
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                title="Drag to Draw Circle on Canvas (O)"
              >
                <Circle className="w-3.5 h-3.5" />
              </button>

              <button
                onClick={() => {
                  setActiveCreationTool(activeCreationTool === 'triangle' ? null : 'triangle');
                  if (drawingMode) setDrawingMode(null);
                }}
                className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                  activeCreationTool === 'triangle'
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                title="Drag to Draw Triangle on Canvas"
              >
                <Triangle className="w-3.5 h-3.5" />
              </button>

              <button
                onClick={() => {
                  setActiveCreationTool(activeCreationTool === 'polygon' ? null : 'polygon');
                  if (drawingMode) setDrawingMode(null);
                }}
                className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                  activeCreationTool === 'polygon'
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                title="Drag to Draw Polygon (Hexagon) on Canvas"
              >
                <Hexagon className="w-3.5 h-3.5" />
              </button>

              <button
                onClick={() => {
                  setActiveCreationTool(activeCreationTool === 'star' ? null : 'star');
                  if (drawingMode) setDrawingMode(null);
                }}
                className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                  activeCreationTool === 'star'
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                title="Drag to Draw Star on Canvas"
              >
                <Star className="w-3.5 h-3.5" />
              </button>

              <button
                onClick={() => {
                  setActiveCreationTool(activeCreationTool === 'line' ? null : 'line');
                  if (drawingMode) setDrawingMode(null);
                }}
                className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                  activeCreationTool === 'line'
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                title="Drag to Draw Line on Canvas (L)"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>

              <button
                onClick={() => {
                  setActiveCreationTool(null);
                  startManualDrawing('unified', 'pen');
                }}
                className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                  drawingMode && subDrawTool === 'pen'
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                title="Vector Pen Tool (P)"
              >
                <PenTool className="w-3.5 h-3.5" />
              </button>

              {/* Simplify Path Tool in Vector Creation Cluster */}
              <button
                onClick={handleOpenSimplifyPath}
                className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                  showSimplifyPathModal
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : selectedElementIds.some(id => {
                        const el = template.elements.find(e => e.id === id);
                        return el && (el.type === 'pen' || el.type === 'path' || Boolean(el.points && el.points.length > 0) || Boolean(el.pathData || el.pathD));
                      })
                    ? 'text-emerald-400 hover:text-white hover:bg-emerald-600/30'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                title="Simplify Path Tool: Reduce anchor points & smooth bezier curves on complex vector shapes"
              >
                <SimplifyPathIcon className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Consolidated Pathfinder Toolset Panel in Main Design Header */}
            <div 
              id="tour-pathfinder-tools"
              className={`flex items-center rounded-lg border p-0.5 gap-0.5 transition-all shrink-0 ${
                selectedElementIds.length >= 2 
                  ? 'bg-indigo-950/60 border-indigo-500/60 shadow-sm shadow-indigo-500/20' 
                  : 'bg-zinc-950 border-zinc-800'
              }`}
            >
              {/* Pathfinder Label Indicator */}
              <div className="hidden xl:flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider select-none border-r border-zinc-800/80 mr-0.5">
                <span className={selectedElementIds.length >= 2 ? 'text-indigo-300 font-semibold' : 'text-zinc-500'}>
                  Pathfinder
                </span>
                {selectedElementIds.length >= 2 && (
                  <span className="text-[9px] font-mono px-1 py-0.2 rounded bg-indigo-500 text-white font-bold leading-none">
                    {selectedElementIds.length}
                  </span>
                )}
              </div>

              {/* Unite / Union */}
              <button
                disabled={selectedElementIds.length < 2}
                onClick={() => handleApplyPathfinder('unite')}
                className={`p-1.5 rounded-md transition-all cursor-pointer disabled:cursor-not-allowed ${
                  selectedElementIds.length >= 2
                    ? 'text-indigo-300 hover:text-white hover:bg-indigo-600/30'
                    : 'text-zinc-600 hover:text-zinc-600 opacity-40'
                }`}
                title={selectedElementIds.length >= 2 ? "Pathfinder: Unite (Union) - Combine selected shapes into a single vector path" : "Pathfinder Unite: Select 2+ overlapping shapes to merge"}
              >
                <PathfinderUnionIcon className="w-3.5 h-3.5" />
              </button>

              {/* Subtract / Minus Front */}
              <button
                disabled={selectedElementIds.length < 2}
                onClick={() => handleApplyPathfinder('subtract')}
                className={`p-1.5 rounded-md transition-all cursor-pointer disabled:cursor-not-allowed ${
                  selectedElementIds.length >= 2
                    ? 'text-rose-300 hover:text-white hover:bg-rose-600/30'
                    : 'text-zinc-600 hover:text-zinc-600 opacity-40'
                }`}
                title={selectedElementIds.length >= 2 ? "Pathfinder: Subtract (Minus Front) - Cut out top shapes from bottom shape" : "Pathfinder Subtract: Select 2+ overlapping shapes to cut out"}
              >
                <PathfinderSubtractIcon className="w-3.5 h-3.5" />
              </button>

              {/* Intersect */}
              <button
                disabled={selectedElementIds.length < 2}
                onClick={() => handleApplyPathfinder('intersect')}
                className={`p-1.5 rounded-md transition-all cursor-pointer disabled:cursor-not-allowed ${
                  selectedElementIds.length >= 2
                    ? 'text-amber-300 hover:text-white hover:bg-amber-600/30'
                    : 'text-zinc-600 hover:text-zinc-600 opacity-40'
                }`}
                title={selectedElementIds.length >= 2 ? "Pathfinder: Intersect - Keep only the overlapping intersection geometry" : "Pathfinder Intersect: Select 2+ overlapping shapes to extract overlap"}
              >
                <PathfinderIntersectIcon className="w-3.5 h-3.5" />
              </button>

              {/* Exclude / XOR */}
              <button
                disabled={selectedElementIds.length < 2}
                onClick={() => handleApplyPathfinder('exclude')}
                className={`p-1.5 rounded-md transition-all cursor-pointer disabled:cursor-not-allowed ${
                  selectedElementIds.length >= 2
                    ? 'text-emerald-300 hover:text-white hover:bg-emerald-600/30'
                    : 'text-zinc-600 hover:text-zinc-600 opacity-40'
                }`}
                title={selectedElementIds.length >= 2 ? "Pathfinder: Exclude (XOR) - Cut out overlap, keep non-overlapping areas" : "Pathfinder Exclude: Select 2+ overlapping shapes"}
              >
                <PathfinderExcludeIcon className="w-3.5 h-3.5" />
              </button>

              {/* Panel Toggle / More Tools */}
              <button
                onClick={() => setShowPathfinderPanel(prev => !prev)}
                className={`p-1.5 rounded-md transition-colors cursor-pointer ${
                  showPathfinderPanel
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                title="More Pathfinder Operations (Divide, Trim, Merge, Crop, Outline, Cutline)"
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </button>

              <div className="h-3.5 w-px bg-zinc-800/80 mx-0.5"></div>

              {/* Simplify Path Quick Button in Pathfinder Bar */}
              <button
                onClick={handleOpenSimplifyPath}
                className={`p-1.5 rounded-md transition-all cursor-pointer ${
                  showSimplifyPathModal
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'text-zinc-400 hover:text-emerald-300 hover:bg-emerald-600/20'
                }`}
                title="Simplify Path: Reduce anchor points & eliminate jitter while preserving curve integrity"
              >
                <SimplifyPathIcon className="w-3.5 h-3.5" />
              </button>
            </div>
            
            <div className="h-4 w-px bg-zinc-800 mx-0.5"></div>
            
            {/* Zoom Controls */}
            <div className="flex items-center bg-zinc-950 rounded-lg border border-zinc-800 px-1 py-0.5 text-xs text-zinc-300">
              <button
                onClick={() => {
                  setZoomLevel(prev => Math.max(0.1, Math.round((prev - 0.1) * 10) / 10));
                  setIsFitToPage(false);
                }}
                className="p-1 hover:text-white rounded cursor-pointer"
                title="Zoom Out (-10%)"
              >
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <span 
                className="px-1.5 font-mono text-[11px] font-semibold cursor-pointer select-none text-zinc-200"
                onClick={handleResetView}
                title="Reset View to 100%"
              >
                {Math.round(zoomLevel * 100)}%
              </span>
              <button
                onClick={() => {
                  setZoomLevel(prev => Math.min(5, Math.round((prev + 0.1) * 10) / 10));
                  setIsFitToPage(false);
                }}
                className="p-1 hover:text-white rounded cursor-pointer"
                title="Zoom In (+10%)"
              >
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
            </div>

            <button
              onClick={handleFitToPage}
              className="p-1.5 rounded-lg text-xs font-medium border transition-colors bg-blue-950/40 text-blue-300 border-blue-900/50 hover:bg-blue-900/60 cursor-pointer"
              title="Fit to Page Width & Height"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>

            <button
              onClick={() => setSnapToGrid(!snapToGrid)}
              className={`p-1.5 rounded-lg text-xs font-medium border transition-colors cursor-pointer ${
                snapToGrid ? 'bg-blue-600/30 text-blue-400 border-blue-500/50' : 'bg-zinc-950 text-zinc-400 border-zinc-800 hover:bg-zinc-800'
              }`}
              title={snapToGrid ? "Snap to 10px Grid (Active)" : "Snap to Grid (Disabled)"}
            >
              <Grid3X3 className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Right: Data record navigator, Preview, 3D Mockup, Document Actions */}
          <div className="flex items-center gap-1.5 shrink-0">
            {csvData && csvData.rows.length > 0 && (
              <RecordSearchJumper
                csvData={csvData}
                currentIndex={previewRecordIndex}
                onSelectRecord={(idx) => setPreviewRecordIndex(idx)}
              />
            )}

            <div className="h-4 w-px bg-zinc-800 mx-0.5"></div>

            <button
              onClick={() => setShowLivePreviewModal(true)}
              className="p-1.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-lg text-xs font-semibold shadow-sm transition-all cursor-pointer"
              title="Live PDF Thumbnail & Bleed Preview"
            >
              <Eye className="w-3.5 h-3.5 text-emerald-100" />
            </button>

            {/* 3D Split View Toggle Button */}
            <button
              onClick={() => {
                const next = !isSplit3DViewOpen;
                setIsSplit3DViewOpen(next);
                try { localStorage.setItem('vdp_split_3d_open', String(next)); } catch {}
              }}
              className={`px-2 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                isSplit3DViewOpen
                  ? 'bg-blue-600 text-white shadow-xs border border-blue-500'
                  : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700'
              }`}
              title="Toggle Live Real-Time 3D Packaging Split View"
            >
              <Box className={`w-3.5 h-3.5 ${isSplit3DViewOpen ? 'text-white' : 'text-blue-400'}`} />
              <span className="hidden md:inline">3D Split</span>
            </button>

            <div className="relative group/threed">
              <button
                id="tour-btn-3d-proof"
                onClick={() => { setShow3DProof(true); setContextMenu(null); }}
                className="p-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-lg text-xs font-semibold shadow-sm transition-all cursor-pointer flex items-center gap-1"
                title="3D Packaging & Label Mockup Studio (Click to Open)"
              >
                <Box className="w-3.5 h-3.5 text-indigo-100" />
                <ChevronDown className="w-3 h-3 text-indigo-200 opacity-70 group-hover/threed:opacity-100" />
              </button>
              
              {/* Quick 3D Settings Dropdown on Hover */}
              <div className="absolute right-0 top-full mt-2 w-60 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl opacity-0 invisible group-hover/threed:opacity-100 group-hover/threed:visible transition-all z-[9999] p-3 flex flex-col gap-2.5">
                <div>
                  <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1 block">Packaging Model</label>
                  <select 
                    className="w-full bg-zinc-950 border border-zinc-800 text-zinc-200 text-xs px-2 py-1.5 rounded outline-none focus:border-indigo-500 cursor-pointer"
                    value={threeD.selectedModel}
                    onChange={(e) => threeD.update3DState({ selectedModel: e.target.value })}
                  >
                    <option value="bottle">Glass Beverage Bottle</option>
                    <option value="can">Aluminum Soda/Beer Can</option>
                    <option value="pouch">Stand-Up Foil Pouch</option>
                    <option value="box">Product Shipping Box</option>
                    <option value="jar">Cosmetic Glass Jar</option>
                    <option value="cup">Coffee Cup / Tumbler</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1 block">Material Finish</label>
                  <select 
                    className="w-full bg-zinc-950 border border-zinc-800 text-zinc-200 text-xs px-2 py-1.5 rounded outline-none focus:border-indigo-500 cursor-pointer"
                    value={threeD.selectedFinish}
                    onChange={(e) => threeD.update3DState({ selectedFinish: e.target.value })}
                  >
                    <option value="gloss">High-Gloss Clear Varnish</option>
                    <option value="matte">Soft-Touch Velvet Matte</option>
                    <option value="gold_foil">Metallic Gold Foil Stamp</option>
                    <option value="silver_foil">Metallic Silver Foil Stamp</option>
                    <option value="clear_film">Clear Transparent Film</option>
                    <option value="kraft">Textured Kraft Substrate</option>
                  </select>
                </div>
              </div>
            </div>

            <button
              onClick={() => handleNewDocument()}
              className="p-1.5 bg-zinc-800 border border-zinc-700 hover:bg-zinc-700 text-zinc-200 rounded-lg text-xs font-medium transition-all cursor-pointer"
              title="Create New Blank Document"
            >
              <Plus className="w-3.5 h-3.5 text-blue-400" />
            </button>

            <button
              onClick={handleQuickSave}
              className="p-1.5 bg-zinc-800 border border-zinc-700 hover:bg-zinc-700 text-zinc-200 rounded-lg text-xs font-medium transition-all cursor-pointer"
              title="Save Template"
            >
              <Save className="w-3.5 h-3.5 text-emerald-400" />
            </button>

            <button
              onClick={() => {
                setShowSaveModeInManager(false);
                setShowTemplateManager(true);
              }}
              className="p-1.5 bg-zinc-800 border border-zinc-700 hover:bg-zinc-700 text-zinc-200 rounded-lg text-xs font-medium transition-all cursor-pointer"
              title="Browse Presets & Saved Templates"
            >
              <FolderOpen className="w-3.5 h-3.5 text-amber-400" />
            </button>
          </div>
        </div>

        {/* Docked Alignment Toolbar for Quick Selection Alignment */}
        {selectedElements.length > 0 && !drawingMode && (
          <TopAlignmentToolbar
            selectedElements={panelSelectedElements}
            canvasWidth={template.width || 300}
            canvasHeight={template.height || 200}
            onBatchUpdateElements={handleBatchUpdateElements}
            onGroupSelected={handleGroupSelectedElements}
            onUngroupSelected={handleUngroupSelectedElements}
          />
        )}

        {/* Canvas Workspace Stage Wrapper */}
        <RulerAndGuides
          canvasWidth={template.width || 300}
          canvasHeight={template.height || 200}
          zoomLevel={zoomLevel}
          panOffset={panOffset}
          appUnit={appUnit}
          setAppUnit={setAppUnit}
          guides={guides}
          gridDotSpacing={gridDotSpacing}
          onAddGuide={handleAddGuide}
          onUpdateGuide={handleUpdateGuide}
          onRemoveGuide={handleRemoveGuide}
          onClearGuides={handleClearGuides}
          showGuides={showGuides}
          onToggleGuides={handleToggleGuides}
          mouseCanvasPos={mouseCanvasPos}
        >
          <div 
            id="tour-design-canvas"
            ref={stageContainerRef}
            onPointerDown={handleStagePointerDown}
            onAuxClick={(e) => { if (e.button === 1) e.preventDefault(); }}
            onMouseDown={(e) => { if (e.button === 1) e.preventDefault(); }}
            className={`w-full h-full relative flex items-center justify-center custom-scrollbar ${
              isHandToolActive || isPanning ? (isPanning ? 'cursor-grabbing select-none' : 'cursor-grab select-none') : ''
            }`}
          >
          {/* Floating Unified Contextual Toolbar (Quick Tools + Contextual Alignment in a single row with freeform persistent position) */}
          {!show3DProof && (
            <UnifiedContextualToolbar
              isPreviewActive={isPreviewCanvasActive}
              onTogglePreview={handleTogglePreview}
              isHandToolActive={isHandToolActive}
              onToggleHandTool={handleToggleHandTool}
              onResetView={handleResetView}
              onOpenLivePreviewModal={handleOpenLivePreviewModal}
              onOpenPathfinderPanel={() => setShowPathfinderPanel(prev => !prev)}
              isPathfinderOpen={showPathfinderPanel}
              onOpenSimplifyPath={handleOpenSimplifyPath}
              isSimplifyPathOpen={showSimplifyPathModal}
              selectedElements={panelSelectedElements}
              canvasWidth={template.width || 300}
              canvasHeight={template.height || 200}
              onBatchUpdateElements={handleBatchUpdateElements}
              onToggleSkipDisable={handleToggleSkipDisableSelected}
              onDuplicateSelected={handleDuplicateSelected}
              onDeleteSelected={handleDeleteSelected}
              onMoveLayerUp={handleMoveSelectedLayerUp}
              onMoveLayerDown={handleMoveSelectedLayerDown}
              onToggleLockSelected={handleToggleLockSelected}
              onGroupSelected={handleGroupSelectedElements}
              onUngroupSelected={handleUngroupSelectedElements}
            />
          )}

          {/* Floating Vector Pen / Pencil Drawing Toolbar */}
          {drawingMode && !show3DProof && (
            <DrawingToolbar
              subDrawTool={subDrawTool}
              setSubDrawTool={setSubDrawTool}
              drawStrokeWidth={drawStrokeWidth}
              setDrawStrokeWidth={setDrawStrokeWidth}
              drawStrokeColor={drawStrokeColor}
              setDrawStrokeColor={setDrawStrokeColor}
              drawFillColor={drawFillColor}
              setDrawFillColor={setDrawFillColor}
              drawClosedPath={drawClosedPath}
              setDrawClosedPath={setDrawClosedPath}
              finishManualDrawing={finishManualDrawing}
              cancelDrawing={() => {
                setDrawingMode(null);
                setDrawingPoints([]);
              }}
              drawingPointsCount={drawingPoints.length}
            />
          )}

          {/* Zoom & Pan Stage Wrapper */}
          <div 
            style={{ 
              transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
              transformOrigin: 'center center',
              transition: isPanning ? 'none' : 'transform 0.05s ease-out'
            }}
            className="relative"
          >
              {/* Physical Sticker Outline */}
              <div
                ref={canvasRef}
                className={`relative shadow-2xl transition-all border border-zinc-300/40 select-none ${
                  activeCreationTool ? 'cursor-crosshair' : ''
                }`}
                style={{
                  width: `${template.width || 300}px`,
                  height: `${template.height || 200}px`,
                  backgroundColor: template.backgroundColor || '#ffffff',
                  borderRadius: template.shape === 'circle' ? '50%' : template.shape === 'oval' ? '50%' : `${template.cornerRadius || 0}px`,
                  overflow: 'hidden'
                }}
                onPointerDown={handleCanvasPointerDown}
                onPointerMove={handleCanvasPointerMove}
                onPointerLeave={handleCanvasPointerLeave}
                onContextMenu={(e) => {
                  e.stopPropagation();
                  handleContextMenu(e, null);
                }}
              >
              {/* Elements (supports real-time Simplify Path preview) */}
              {displayElements.map((el) => {
                const isSelected = selectedElementIds.includes(el.id);
                return (
                  <CanvasElementItem
                    editingTextId={editingTextId}
                    setEditingTextId={setEditingTextId}
                    key={el.id}
                    el={el}
                    isSelected={isSelected}
                    isPreviewCanvasActive={isPreviewCanvasActive}
                    isHandToolActive={isHandToolActive}
                    showLivePreview={showLivePreview}
                    previewRecordIndex={previewRecordIndex}
                    csvData={csvData}
                    isSingleSelection={selectedElementIds.length === 1}
                    onPointerDown={handlePointerDown}
                    onContextMenu={handleContextMenu}
                    onDoubleClick={handleElementDoubleClick}
                    onResizeStart={handleResizeStart}
                    onRotateStart={handleRotateStart}
                    onUpdateElement={handleUpdateElement}
                    onControlPointDrag={handleControlPointDrag}
                    onAnchorPointDrag={handleAnchorPointDrag}
                    onToggleAnchorPointType={handleToggleAnchorPointType}
                    onEnsureAnchorHandles={handleEnsureAnchorHandles}
                  />
                );
              })}

              {/* Active Vector Drawing Live Canvas Overlay */}
              {drawingMode && (
                <svg className="absolute inset-0 w-full h-full pointer-events-none z-50 overflow-visible">
                  {/* Live Path */}
                  {drawingPoints.length > 0 && (
                    <path
                      d={
                        subDrawTool === 'pen'
                          ? bezierPointsToSvgPath(
                              mouseCanvasPos && drawingPoints.length > 0 && !isPenDraggingHandle
                                ? [...drawingPoints, { x: mouseCanvasPos.x, y: mouseCanvasPos.y }]
                                : drawingPoints,
                              drawClosedPath
                            )
                          : pointsToSvgPath(drawingPoints, drawClosedPath, drawSmooth)
                      }
                      fill={drawFillColor !== 'none' ? drawFillColor : 'none'}
                      stroke={drawStrokeColor}
                      strokeWidth={drawStrokeWidth}
                      strokeDasharray="none"
                    />
                  )}

                  {/* Anchor Nodes & Bezier Handles during drawing */}
                  {drawingPoints.map((pt, idx) => {
                    const isStart = idx === 0;
                    const isNearStart = isStart && mouseCanvasPos && Math.hypot(mouseCanvasPos.x - pt.x, mouseCanvasPos.y - pt.y) < 14;

                    return (
                      <g key={idx}>
                        {pt.cp1 && (
                          <>
                            <line x1={pt.x} y1={pt.y} x2={pt.cp1.x} y2={pt.cp1.y} stroke="#2563eb" strokeWidth={0.5} />
                            <circle cx={pt.cp1.x} cy={pt.cp1.y} r={2} fill="#ffffff" stroke="#2563eb" strokeWidth={0.5} />
                          </>
                        )}
                        {pt.cp2 && (
                          <>
                            <line x1={pt.x} y1={pt.y} x2={pt.cp2.x} y2={pt.cp2.y} stroke="#2563eb" strokeWidth={0.5} />
                            <circle cx={pt.cp2.x} cy={pt.cp2.y} r={2} fill="#ffffff" stroke="#2563eb" strokeWidth={0.5} />
                          </>
                        )}

                        <rect
                          x={pt.x - 2}
                          y={pt.y - 2}
                          width={4}
                          height={4}
                          fill={isStart ? "#22c55e" : "#ffffff"}
                          stroke={isStart ? "#15803d" : "#1d4ed8"}
                          strokeWidth={0.5}
                          rx={0.5}
                        />

                        {isNearStart && (
                          <circle
                            cx={pt.x}
                            cy={pt.y}
                            r={5}
                            fill="rgba(34, 197, 94, 0.2)"
                            stroke="#22c55e"
                            strokeWidth={0.5}
                          />
                        )}
                      </g>
                    );
                  })}
                </svg>
              )}

              {/* Multi-Select Group Bounding Box Overlay */}
              {selectedElements.length > 1 && !marqueeBox && (() => {
                const gb = getElementsBoundingBox(selectedElements);
                const isGrouped = selectedElements.some(e => Boolean(e.groupId));
                return (
                  <div
                    className="absolute border border-dashed border-blue-600 bg-blue-500/5 pointer-events-none z-30 rounded-xs shadow-xs"
                    style={{
                      left: `${gb.minX - 3}px`,
                      top: `${gb.minY - 3}px`,
                      width: `${gb.width + 6}px`,
                      height: `${gb.height + 6}px`,
                    }}
                  >
                    {/* Group Label Badge */}
                    <div className="absolute -top-7 left-0 bg-blue-600 text-white text-[10px] font-semibold px-2 py-0.5 rounded shadow-md flex items-center gap-1.5 whitespace-nowrap pointer-events-auto">
                      <FolderPlus className="w-3 h-3 shrink-0" />
                      <span>{isGrouped ? 'Grouped Unit' : 'Multiple Selected'} ({selectedElements.length} items)</span>
                      <span className="text-blue-200 border-l border-blue-400/50 pl-1.5">{Math.round(gb.width)} × {Math.round(gb.height)} px</span>
                      {isGrouped ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUngroupSelectedElements();
                          }}
                          className="ml-1 px-1.5 py-0.5 bg-amber-500 hover:bg-amber-400 text-zinc-950 rounded text-[9px] uppercase font-extrabold transition-colors cursor-pointer"
                          title="Ungroup elements (Ctrl+Shift+G)"
                        >
                          Ungroup
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGroupSelectedElements();
                          }}
                          className="ml-1 px-1.5 py-0.5 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded text-[9px] uppercase font-extrabold transition-colors cursor-pointer"
                          title="Group elements together (Ctrl+G)"
                        >
                          Group
                        </button>
                      )}
                    </div>

                    {/* Group Corner Anchor Handles (0.5px) */}
                    <div className="absolute -top-1 -left-1 w-1.5 h-1.5 bg-white border-[0.5px] border-blue-600" />
                    <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-white border-[0.5px] border-blue-600" />
                    <div className="absolute -bottom-1 -left-1 w-1.5 h-1.5 bg-white border-[0.5px] border-blue-600" />
                    <div className="absolute -bottom-1 -right-1 w-1.5 h-1.5 bg-white border-[0.5px] border-blue-600" />
                  </div>
                );
              })()}

              {/* Interactive Drag-to-Create Bounding Box Preview (Clean 0.5px Thin Vector Style) */}
              {creationPreviewBox && (() => {
                const boxX = Math.min(creationPreviewBox.startX, creationPreviewBox.currentX);
                const boxY = Math.min(creationPreviewBox.startY, creationPreviewBox.currentY);
                const boxW = Math.max(1, Math.abs(creationPreviewBox.currentX - creationPreviewBox.startX));
                const boxH = Math.max(1, Math.abs(creationPreviewBox.currentY - creationPreviewBox.startY));
                const toolName = activeCreationTool === 'textbox' ? 'Text Box' : activeCreationTool === 'text' ? 'Text' : activeCreationTool ? activeCreationTool.charAt(0).toUpperCase() + activeCreationTool.slice(1) : '';

                return (
                  <div
                    className="absolute border-[0.5px] border-blue-500 bg-transparent pointer-events-none z-50 rounded-xs"
                    style={{
                      left: `${boxX}px`,
                      top: `${boxY}px`,
                      width: `${boxW}px`,
                      height: `${boxH}px`,
                      borderRadius: activeCreationTool === 'circle' ? '50%' : '1px',
                    }}
                  >
                    {/* 4 Crisp 0.5px Corner Indicators */}
                    <div className="absolute -top-1 -left-1 w-1.5 h-1.5 bg-white border-[0.5px] border-blue-600" />
                    <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-white border-[0.5px] border-blue-600" />
                    <div className="absolute -bottom-1 -left-1 w-1.5 h-1.5 bg-white border-[0.5px] border-blue-600" />
                    <div className="absolute -bottom-1 -right-1 w-1.5 h-1.5 bg-white border-[0.5px] border-blue-600" />

                    <div className="absolute -top-6 left-0 bg-blue-600 text-white font-mono text-[10px] font-semibold px-1.5 py-0.5 rounded shadow-sm whitespace-nowrap">
                      {toolName}: {Math.round(boxW)} × {Math.round(boxH)} px
                    </div>
                  </div>
                );
              })()}

              {/* Marquee Drag Selection Box Overlay */}
              {marqueeBox && (() => {
                const boxX = Math.min(marqueeBox.startX, marqueeBox.currentX);
                const boxY = Math.min(marqueeBox.startY, marqueeBox.currentY);
                const boxW = Math.abs(marqueeBox.currentX - marqueeBox.startX);
                const boxH = Math.abs(marqueeBox.currentY - marqueeBox.startY);

                return (
                  <div
                    className="absolute border-2 border-dashed border-blue-500 bg-blue-500/20 rounded-sm pointer-events-none z-50 shadow-sm"
                    style={{
                      left: `${boxX}px`,
                      top: `${boxY}px`,
                      width: `${boxW}px`,
                      height: `${boxH}px`
                    }}
                  />
                );
              })()}
            </div>
          </div>
        </div>
      </RulerAndGuides>
      </div>

      {/* Live 3D Packaging Split Studio Viewer */}
      {isSplit3DViewOpen && (
        <div className="w-80 xl:w-96 h-full border-l border-zinc-800 z-15 shrink-0 hidden md:flex flex-col animate-in fade-in duration-200">
          <Studio3DSplitViewer
            template={template}
            csvData={csvData}
            previewRecordIndex={previewRecordIndex}
            onOpenFullScreenModal={() => setShow3DProof(true)}
            onClose={() => {
              setIsSplit3DViewOpen(false);
              try { localStorage.setItem('vdp_split_3d_open', 'false'); } catch {}
            }}
          />
        </div>
      )}

      {/* Right Sidebar */}
      <div id="tour-properties-panel" className={`${isRightSidebarCollapsed ? 'w-16' : 'w-72 xl:w-80'} bg-zinc-900 border-l border-zinc-800 flex flex-col shrink-0 shadow-lg z-20 h-full absolute lg:relative transition-[width] duration-300 ease-in-out right-0 ${isRightSidebarCollapsed ? 'overflow-visible' : 'overflow-hidden'}`}>
        
        {isRightSidebarCollapsed && (
          <div className="flex flex-col items-center py-4 gap-4 h-full w-16 shrink-0 absolute right-0 top-0 z-10 bg-zinc-900 border-l border-zinc-800">
            <button onClick={toggleRightSidebar} className="p-2 text-zinc-400 hover:text-white rounded-lg hover:bg-zinc-800 transition-colors" title="Expand Properties Panel">
              <PanelRightOpen className="w-5 h-5" />
            </button>
            <div className="w-8 h-[1px] bg-zinc-800 my-1" />
            <button onClick={() => { setActiveRightFlyout(activeRightFlyout === 'properties' ? null : 'properties'); setSidebarTab('properties'); setIsPropertiesLayersExpanded(true); }} className={`p-2 rounded-lg hover:bg-zinc-800 transition-colors ${activeRightFlyout === 'properties' ? 'text-blue-400' : 'text-zinc-400 hover:text-white'}`} title="Properties">
              <Settings2 className="w-5 h-5" />
            </button>
            <button onClick={() => { setActiveRightFlyout(activeRightFlyout === 'layers' ? null : 'layers'); setSidebarTab('layers'); setIsPropertiesLayersExpanded(true); }} className={`p-2 rounded-lg hover:bg-zinc-800 transition-colors ${activeRightFlyout === 'layers' ? 'text-blue-400' : 'text-zinc-400 hover:text-white'}`} title="Layers">
              <Layers className="w-5 h-5" />
            </button>
          </div>
        )}

        <AnimatePresence>
          {(!isRightSidebarCollapsed || activeRightFlyout) && (
            <motion.div 
              className={`${isRightSidebarCollapsed ? 'absolute right-16 top-0 bottom-0 w-72 xl:w-80 shadow-2xl border-l border-zinc-800 z-50 bg-zinc-900 flex flex-col h-full overflow-y-auto' : 'flex-1 flex flex-col w-full h-full'}`}
              variants={macPanelVariants('right')}
              initial={isRightSidebarCollapsed ? "initial" : false}
              animate={isRightSidebarCollapsed ? "animate" : false}
              exit={isRightSidebarCollapsed ? "exit" : false}
              transition={macSpring}
            >
        {/* 3. Properties / Layers Section */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex items-center border-b border-zinc-800 bg-zinc-900/80">
            <button 
              onClick={toggleRightSidebar}
              className="p-2.5 text-zinc-500 hover:text-zinc-300 border-r border-zinc-800 shrink-0 transition-colors flex items-center justify-center"
              title="Collapse Panel"
            >
              <PanelRightClose className="w-4 h-4" />
            </button>
            <button 
              onClick={() => setIsPropertiesLayersExpanded(!isPropertiesLayersExpanded)}
              className="p-2 text-zinc-400 hover:text-white border-r border-zinc-800 shrink-0 transition-colors flex items-center gap-1"
              title={isPropertiesLayersExpanded ? "Collapse View" : "Expand View"}
            >
              {isPropertiesLayersExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            <button 
              onClick={() => { setSidebarTab('properties'); if (!isPropertiesLayersExpanded) setIsPropertiesLayersExpanded(true); }}
              className={`flex-1 py-2.5 text-xs font-semibold uppercase tracking-wider flex items-center justify-center gap-2 transition-colors ${sidebarTab === 'properties' ? 'text-blue-400 border-b-2 border-blue-500 bg-blue-500/10' : 'text-zinc-500 hover:text-zinc-300'}`}
            >
              <Settings2 className="w-3.5 h-3.5" /> Properties
            </button>
            <button 
              onClick={() => { setSidebarTab('layers'); if (!isPropertiesLayersExpanded) setIsPropertiesLayersExpanded(true); }}
              className={`flex-1 py-2.5 text-xs font-semibold uppercase tracking-wider flex items-center justify-center gap-2 transition-colors ${sidebarTab === 'layers' ? 'text-blue-400 border-b-2 border-blue-500 bg-blue-500/10' : 'text-zinc-500 hover:text-zinc-300'}`}
            >
              <Layers className="w-3.5 h-3.5" /> Layers ({template.elements.length})
            </button>
          </div>

          {isPropertiesLayersExpanded && (
            <div className="flex-1 overflow-y-auto">
              {sidebarTab === 'layers' ? (
                <div className="p-2 bg-zinc-950 min-h-full">
                  {template.elements.length === 0 ? (
                     <div className="p-4 flex flex-col items-center justify-center text-zinc-500 text-sm text-center min-h-[150px]">
                       <Layers className="w-8 h-8 text-zinc-700 mb-2" />
                       <p>No layers yet. Add elements to the canvas.</p>
                     </div>
                  ) : (
                     <div className="space-y-1">
                       {[...template.elements].reverse().map((el, index) => {
                         const isSelected = selectedElementIds.includes(el.id);
                         const isHidden = el.disabled || el.isSkipped;
                         const originalIndex = template.elements.length - 1 - index;
                         return (
                           <div 
                             key={el.id}
                             draggable
                             onDragStart={(e) => {
                               setDraggedLayerId(el.id);
                               e.dataTransfer.effectAllowed = 'move';
                             }}
                             onDragOver={(e) => {
                               e.preventDefault();
                               e.dataTransfer.dropEffect = 'move';
                             }}
                             onDrop={(e) => {
                               e.preventDefault();
                               if (draggedLayerId && draggedLayerId !== el.id) {
                                 const elements = [...template.elements];
                                 const fromIdx = elements.findIndex(e => e.id === draggedLayerId);
                                 const toIdx = elements.findIndex(e => e.id === el.id);
                                 const [moved] = elements.splice(fromIdx, 1);
                                 elements.splice(toIdx, 0, moved);
                                 setTemplate({...template, elements});
                               }
                               setDraggedLayerId(null);
                             }}
                             onClick={() => setSelectedElementIds([el.id])}
                             className={`flex items-center justify-between p-2 rounded cursor-pointer border transition-colors ${isSelected ? 'bg-blue-900/20 border-blue-700' : 'bg-zinc-900 border-zinc-800 hover:border-zinc-600'} ${draggedLayerId === el.id ? 'opacity-50' : ''}`}
                           >
                             <div className="flex items-center gap-2 truncate flex-1">
                               {el.type === 'text' ? <Type className="w-4 h-4 text-zinc-400 shrink-0" /> :
                                el.type === 'qr' ? <QrCode className="w-4 h-4 text-zinc-400 shrink-0" /> :
                                el.type === 'barcode' ? <BarcodeIcon className="w-4 h-4 text-zinc-400 shrink-0" /> :
                                el.type === 'image' ? <ImageIcon className="w-4 h-4 text-zinc-400 shrink-0" /> :
                                el.type === 'rectangle' ? <Square className="w-4 h-4 text-zinc-400 shrink-0" /> :
                                el.type === 'circle' ? <Circle className="w-4 h-4 text-zinc-400 shrink-0" /> :
                                <Box className="w-4 h-4 text-zinc-400 shrink-0" />}

                                {Boolean(el.groupId) && (
                                  <FolderPlus className="w-3 h-3 text-blue-400 shrink-0" title="Grouped element" />
                                )}
                               
                               {editingLayerId === el.id ? (
                                 <input
                                   autoFocus
                                   type="text"
                                   className="text-xs bg-zinc-950 border border-blue-500 rounded px-1 w-full text-zinc-200 outline-none"
                                   defaultValue={el.name || el.text || el.columnName || el.type}
                                   onBlur={(e) => {
                                     handleUpdateElement(el.id, { name: e.target.value });
                                     setEditingLayerId(null);
                                   }}
                                   onKeyDown={(e) => {
                                     if (e.key === 'Enter') {
                                       handleUpdateElement(el.id, { name: e.currentTarget.value });
                                       setEditingLayerId(null);
                                     }
                                   }}
                                   onClick={(e) => e.stopPropagation()}
                                 />
                               ) : (
                                 <span 
                                   onDoubleClick={(e) => {
                                     e.stopPropagation();
                                     setEditingLayerId(el.id);
                                   }}
                                   className={`text-xs truncate flex-1 ${isHidden ? 'text-zinc-600 line-through' : 'text-zinc-300'}`}
                                   title="Double click to rename"
                                 >
                                   {el.name || el.text || el.columnName || el.type}
                                 </span>
                               )}
                             </div>
                             <div className="flex items-center gap-1 shrink-0 ml-2">
                               <button 
                                 onClick={(e) => { e.stopPropagation(); handleUpdateElement(el.id, { disabled: !el.disabled, isSkipped: !el.isSkipped }); }}
                                 className={`p-1 rounded hover:bg-zinc-700 ${isHidden ? 'text-zinc-600' : 'text-zinc-400 hover:text-zinc-200'}`}
                                 title={isHidden ? 'Show Layer' : 'Hide Layer'}
                               >
                                 {isHidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                               </button>
                               <button 
                                 onClick={(e) => { e.stopPropagation(); handleUpdateElement(el.id, { locked: !el.locked }); }}
                                 className={`p-1 rounded hover:bg-zinc-700 ${el.locked ? 'text-rose-400' : 'text-zinc-400 hover:text-zinc-200'}`}
                                 title={el.locked ? 'Unlock Layer' : 'Lock Layer'}
                               >
                                 {el.locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                               </button>
                               <div className="flex flex-col ml-1 border-l border-zinc-700 pl-1">
                                 <button 
                                   onClick={(e) => { 
                                     e.stopPropagation(); 
                                     const elements = [...template.elements];
                                     const idx = elements.findIndex(e => e.id === el.id);
                                     if (idx < elements.length - 1) {
                                       const temp = elements[idx];
                                       elements[idx] = elements[idx+1];
                                       elements[idx+1] = temp;
                                       setTemplate({...template, elements});
                                     }
                                   }}
                                   className={`p-0.5 rounded text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700 ${originalIndex === template.elements.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
                                   title="Bring Forward"
                                 >
                                   <ArrowUp className="w-3 h-3" />
                                 </button>
                                 <button 
                                   onClick={(e) => { 
                                     e.stopPropagation(); 
                                     const elements = [...template.elements];
                                     const idx = elements.findIndex(e => e.id === el.id);
                                     if (idx > 0) {
                                       const temp = elements[idx];
                                       elements[idx] = elements[idx-1];
                                       elements[idx-1] = temp;
                                       setTemplate({...template, elements});
                                     }
                                   }}
                                   className={`p-0.5 rounded text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700 ${originalIndex === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
                                   title="Send Backward"
                                 >
                                   <ArrowDown className="w-3 h-3" />
                                 </button>
                               </div>
                             </div>
                           </div>
                         );
                       })}
                     </div>
                  )}
                </div>
              ) : selectedElements.length > 0 ? (
                <div className="p-4 flex-1 overflow-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-zinc-200 flex items-center gap-2 text-sm">
                      <Settings2 className="w-4 h-4 text-blue-400" /> Element Properties
                    </h3>
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => {
                          const allLocked = selectedElements.every(e => e.locked);
                          selectedElementIds.forEach(id => handleUpdateElement(id, { locked: !allLocked }));
                        }}
                        className={`p-1.5 rounded transition-colors ${selectedElements.some(e => e.locked) ? 'text-blue-600 bg-blue-50 hover:bg-blue-100' : 'text-zinc-400 hover:bg-zinc-800'}`}
                        title={selectedElements.every(e => e.locked) ? "Unlock Selection" : "Lock Selection"}
                      >
                        {selectedElements.every(e => e.locked) ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                      </button>
                      <button 
                        onClick={() => handleDuplicateElement(selectedElementIds)}
                        className="p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-300 rounded transition-colors"
                        title="Duplicate (Ctrl+D)"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDeleteElement(selectedElementIds)}
                        className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Delete (Del)"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-4">
                    <PropertiesPanel
                      selectedElements={panelSelectedElements}
                      onUpdateElements={handleUpdateSelectedElements}
                      onBatchUpdateElements={handleBatchUpdateElements}
                      canvasWidth={template.width || 300}
                      canvasHeight={template.height || 200}
                      appUnit={appUnit}
                      csvData={csvData}
                      previewRecordIndex={previewRecordIndex}
                      onGroupSelected={handleGroupSelectedElements}
                      onUngroupSelected={handleUngroupSelectedElements}
                      brandPalette={template.brandPalette || DEFAULT_BRAND_PALETTE}
                      onApplyPathfinder={handleApplyPathfinder}
                      onTogglePathfinderPanel={() => setShowPathfinderPanel(prev => !prev)}
                    />
                  </div>
                </div>
              ) : (
                <PriceTagSidebarManager
                  template={panelTemplate}
                  onUpdateTemplate={handleUpdateTemplateProperties}
                  onUpdateElement={handleUpdateElement}
                  onAddElement={handleAddElement}
                  onSelectElement={handleSelectElementById}
                  appUnit={appUnit}
                  setAppUnit={setAppUnit}
                  csvData={csvData}
                />
              )}
            </div>
          )}
        </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {/* Properties Modal Popup */}
      <MacWindow 
        isOpen={showPropertiesModal && selectedElement !== null} 
        onClose={() => setShowPropertiesModal(false)} 
        title={`${selectedElement ? selectedElement.type.charAt(0).toUpperCase() + selectedElement.type.slice(1) : 'Element'} Properties`}
        icon={<Settings2 className="w-5 h-5 text-blue-500" />}
        maxWidth="max-w-6xl"
      >
        <div className="flex flex-col w-full h-full max-h-[85vh]">
          <div className="p-5 overflow-auto flex-1 bg-zinc-950">
            {selectedElements.length > 0 && (
              <PropertiesPanel 
                selectedElements={panelSelectedElements} 
                onUpdateElements={handleUpdateSelectedElements} 
                onBatchUpdateElements={handleBatchUpdateElements}
                canvasWidth={template.width || 300}
                canvasHeight={template.height || 200}
                appUnit={appUnit} 
                csvData={csvData} 
                previewRecordIndex={previewRecordIndex}
                onGroupSelected={handleGroupSelectedElements}
                onUngroupSelected={handleUngroupSelectedElements}
                brandPalette={template.brandPalette || DEFAULT_BRAND_PALETTE}
                isModalMode={true}
                onApplyPathfinder={handleApplyPathfinder}
                onTogglePathfinderPanel={() => setShowPathfinderPanel(prev => !prev)}
              />
            )}
          </div>
          <div className="p-4 border-t border-zinc-800 flex justify-end bg-zinc-900 shrink-0">
            <button onClick={() => setShowPropertiesModal(false)} className="px-6 py-2 bg-[#1473E6] text-white rounded-md font-medium hover:bg-blue-700 shadow-sm cursor-pointer">
              Done
            </button>
          </div>
        </div>
      </MacWindow>
      {/* Date Picker Modal */}
      {showDatePickerModal && (
        <DatePickerModal
          initialValue={selectedElement?.staticValue || getLocalIsoDateString()}
          dateFormat={selectedElement?.dateFormat || 'DD/MM/YYYY'}
          onSelectDate={(formattedStr, _rawIsoStr, formatPattern) => {
            selectedElementIds.forEach(id => handleUpdateElement(id, {
              staticValue: formattedStr,
              ...(formatPattern ? { dateFormat: formatPattern } : {})
            }));
          }}
          onClose={() => setShowDatePickerModal(false)}
        />
      )}

      {/* Template Manager & Presets Modal */}
      <TemplateManagerModal 
        isOpen={showTemplateManager}
        onClose={() => {
          setShowTemplateManager(false);
          setShowSaveModeInManager(false);
        }}
        currentTemplate={template}
        currentLayout={layout}
        currentUnit={appUnit}
        onLoadTemplate={handleLoadTemplateFromModal}
        initialShowSaveForm={showSaveModeInManager}
      />

      {/* Custom Context Menu */}
      <AnimatePresence>
        {contextMenu && (
          <motion.div 
            variants={macPopUpVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="fixed z-[99999] bg-zinc-800 border border-zinc-700 rounded-lg shadow-2xl py-1.5 w-48 text-sm flex flex-col overflow-hidden"
            style={{ top: contextMenu.y, left: contextMenu.x, originX: 0, originY: 0 }}
            onPointerDown={(e) => e.stopPropagation()} // Prevent close on self click
          >
          {contextMenu.elementId ? (
            <>
              <button onClick={() => handleCutElement(contextMenu.elementId!)} className="px-3 py-1.5 text-left text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors flex items-center justify-between">Cut <span className="text-[10px] text-zinc-500">Ctrl+X</span></button>
              <button onClick={() => handleCopyElement(contextMenu.elementId!)} className="px-3 py-1.5 text-left text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors flex items-center justify-between">Copy <span className="text-[10px] text-zinc-500">Ctrl+C</span></button>
              <button onClick={() => { handleDuplicateElement(contextMenu.elementId!); closeContextMenu(); }} className="px-3 py-1.5 text-left text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors flex items-center justify-between">Duplicate <span className="text-[10px] text-zinc-500">Ctrl+D</span></button>
              <button onClick={() => handlePasteElement()} disabled={!clipboard} className="px-3 py-1.5 text-left text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors disabled:opacity-50 flex items-center justify-between">Paste <span className="text-[10px] text-zinc-500">Ctrl+V</span></button>
              <div className="h-[1px] bg-zinc-700 my-1"></div>
              {selectedElementIds.length >= 2 && (
                <>
                  <button 
                    onClick={() => { handleGroupSelectedElements(); closeContextMenu(); }} 
                    className="px-3 py-1.5 text-left text-blue-400 hover:bg-zinc-700 hover:text-blue-300 transition-colors flex items-center justify-between font-medium cursor-pointer"
                  >
                    Group Selection <span className="text-[10px] text-zinc-500">Ctrl+G</span>
                  </button>
                  <button 
                    onClick={() => { handleApplyPathfinder('unite'); closeContextMenu(); }} 
                    className="px-3 py-1.5 text-left text-indigo-400 hover:bg-zinc-700 hover:text-indigo-300 transition-colors flex items-center justify-between font-medium cursor-pointer"
                  >
                    Pathfinder: Unite Shapes
                  </button>
                  <button 
                    onClick={() => { handleApplyPathfinder('subtract'); closeContextMenu(); }} 
                    className="px-3 py-1.5 text-left text-rose-400 hover:bg-zinc-700 hover:text-rose-300 transition-colors flex items-center justify-between font-medium cursor-pointer"
                  >
                    Pathfinder: Subtract Top
                  </button>
                  <button 
                    onClick={() => { setShowPathfinderPanel(true); closeContextMenu(); }} 
                    className="px-3 py-1.5 text-left text-purple-400 hover:bg-zinc-700 hover:text-purple-300 transition-colors flex items-center justify-between font-medium cursor-pointer"
                  >
                    Open Pathfinder Panel...
                  </button>
                  <button 
                    onClick={() => { handleOpenSimplifyPath(); closeContextMenu(); }} 
                    className="px-3 py-1.5 text-left text-emerald-400 hover:bg-zinc-700 hover:text-emerald-300 transition-colors flex items-center justify-between font-medium cursor-pointer"
                  >
                    Simplify Vector Path...
                  </button>
                </>
              )}
              {selectedElementIds.length === 1 && (() => {
                const el = template.elements.find(e => e.id === contextMenu.elementId);
                if (el && (el.type === 'pen' || el.type === 'path' || Boolean(el.points && el.points.length > 0) || Boolean(el.pathData || el.pathD))) {
                  return (
                    <button 
                      onClick={() => { handleOpenSimplifyPath(); closeContextMenu(); }} 
                      className="px-3 py-1.5 text-left text-emerald-400 hover:bg-zinc-700 hover:text-emerald-300 transition-colors flex items-center justify-between font-medium cursor-pointer"
                    >
                      Simplify Vector Path...
                    </button>
                  );
                }
                return null;
              })()}
              {selectedElements.some(e => Boolean(e.groupId)) && (
                <button 
                  onClick={() => { handleUngroupSelectedElements(); closeContextMenu(); }} 
                  className="px-3 py-1.5 text-left text-amber-400 hover:bg-zinc-700 hover:text-amber-300 transition-colors flex items-center justify-between font-medium"
                >
                  Ungroup Selection <span className="text-[10px] text-zinc-500">Ctrl+Shift+G</span>
                </button>
              )}
              {(selectedElementIds.length >= 2 || selectedElements.some(e => Boolean(e.groupId))) && (
                <div className="h-[1px] bg-zinc-700 my-1"></div>
              )}
              <button onClick={() => {
                const el = template.elements.find(e => e.id === contextMenu.elementId);
                if (el) {
                  const newName = window.prompt("Rename element:", el.name || '');
                  if (newName) handleUpdateElement(el.id, { name: newName });
                }
                closeContextMenu();
              }} className="px-3 py-1.5 text-left text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors flex items-center justify-between">Rename...</button>

              {(() => {
                const el = template.elements.find(e => e.id === contextMenu.elementId);
                if (!el) return null;
                if (['text', 'uppercase', 'currency', 'date', 'serial', 'barcode', 'qr', 'datamatrix', 'ean13', 'upca'].includes(el.type)) {
                  return (
                    <button onClick={() => {
                      const newText = window.prompt("Edit Text / Static Value:", el.staticValue || el.text || '');
                      if (newText !== null) handleUpdateElement(el.id, { staticValue: newText, text: newText });
                      closeContextMenu();
                    }} className="px-3 py-1.5 text-left text-amber-300 hover:bg-zinc-700 hover:text-amber-200 transition-colors font-medium">
                      Edit Text Value...
                    </button>
                  );
                }
                if (el.type === 'image') {
                  return (
                    <label className="px-3 py-1.5 text-left text-amber-300 hover:bg-zinc-700 hover:text-amber-200 transition-colors font-medium cursor-pointer block">
                      Browse & Upload Image...
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (evt) => {
                              if (evt.target?.result) {
                                const dataUrl = evt.target.result as string;
                                handleUpdateElement(el.id, { src: dataUrl, staticValue: dataUrl });
                              }
                            };
                            reader.readAsDataURL(file);
                          }
                          closeContextMenu();
                        }} 
                      />
                    </label>
                  );
                }
                return null;
              })()}
              
              <button onClick={() => {
                if (contextMenu.elementId) setSelectedElementIds([contextMenu.elementId]);
                setShowPropertiesModal(true);
                closeContextMenu();
              }} className="px-3 py-1.5 text-left text-blue-400 hover:bg-zinc-700 hover:text-blue-300 transition-colors">Map to CSV Data</button>

              <button onClick={() => {
                if (contextMenu.elementId) setSelectedElementIds([contextMenu.elementId]);
                setShowPropertiesModal(true);
                closeContextMenu();
              }} className="px-3 py-1.5 text-left text-emerald-400 hover:bg-zinc-700 hover:text-emerald-300 transition-colors">Element Properties</button>

              <div className="h-[1px] bg-zinc-700 my-1"></div>
              <button onClick={() => { handleBringForward(); closeContextMenu(); }} className="px-3 py-1.5 text-left text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors">Bring Forward</button>
              <button onClick={() => { handleSendBackward(); closeContextMenu(); }} className="px-3 py-1.5 text-left text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors">Send Backward</button>
              <div className="h-[1px] bg-zinc-700 my-1"></div>
              <button onClick={() => {
                if (contextMenu.elementId) {
                  if (selectedElementIds.includes(contextMenu.elementId)) {
                    handleDeleteElement(selectedElementIds);
                  } else {
                    handleDeleteElement(contextMenu.elementId);
                  }
                }
                closeContextMenu();
              }} className="px-3 py-1.5 text-left text-red-400 hover:bg-zinc-700 hover:text-red-300 transition-colors flex items-center justify-between">Delete <span className="text-[10px] text-red-500/50">Del</span></button>
            </>
          ) : (
            <>
              <button onClick={() => handlePasteElement()} disabled={!clipboard} className="px-3 py-1.5 text-left text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors disabled:opacity-50">Paste</button>
              <div className="h-[1px] bg-zinc-700 my-1"></div>
              <button onClick={() => { handleNewDocument(); closeContextMenu(); }} className="px-3 py-1.5 text-left text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors">New Document</button>
              <button onClick={() => { setSnapToGrid(!snapToGrid); closeContextMenu(); }} className="px-3 py-1.5 text-left text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors">Toggle Grid Snap</button>
              <button onClick={() => { setShowTemplateManager(true); closeContextMenu(); }} className="px-3 py-1.5 text-left text-amber-400 hover:bg-zinc-700 hover:text-amber-300 transition-colors">Presets & Save...</button>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>

      </div>
      {show3DProof && (
        <ProofViewer3DModal isOpen={show3DProof} onClose={() => setShow3DProof(false)} />
      )}
      <PathfinderPanel
        isOpen={showPathfinderPanel}
        onClose={() => setShowPathfinderPanel(false)}
        selectedElements={panelSelectedElements}
        allElements={template.elements}
        setTemplateElements={(newElements) => setTemplate(prev => ({ ...prev, elements: newElements }))}
        setSelectedElementIds={setSelectedElementIds}
        onOpenSimplifyPath={handleOpenSimplifyPath}
      />
      <SimplifyPathModal
        isOpen={showSimplifyPathModal}
        onClose={() => {
          setShowSimplifyPathModal(false);
          setSimplifyPreviewElements(null);
        }}
        selectedElements={panelSelectedElements}
        onApply={handleApplySimplifyPath}
        onPreviewUpdate={handleSimplifyPreviewUpdate}
      />
      <LivePreviewModal isOpen={showLivePreviewModal} 
        onClose={() => setShowLivePreviewModal(false)} 
        template={template}
        layout={layout}
        appUnit={appUnit}
        csvData={csvData}
        previewRecordIndex={previewRecordIndex}
        onUpdateBleed={(bleedMm) => setLayout(prev => ({ ...prev, bleed: bleedMm }))}
      />
    </div>
  );
};
